import React, { useState, useEffect } from 'react';
import { api } from './api/api';
import generateReceiptPdf from './generateReceiptPdf';
import generateBolInvoicePdf from './generateBolInvoicePdf';
import generateAirCargoManifestPdf from './generatManifest';
import { toast } from 'react-toastify'; // أو المكتبة التي تستخدمها للتنبيهات
import { resolveStoragePriceValues } from './utils/storagePricing';


const hasPermission = (permissions, code) => Array.isArray(permissions) && permissions.includes(code);



// Helper to parse JSON arrays or single IDs from DB
const parseIdArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
      return [parsed];
    } catch { return trimmed.split(",").map((item) => item.trim()).filter(Boolean); }
  }
  return [value];
};

// ✅ دوال مساعدة من ManifestScreen
const toDateInput = (d) => {
  if (!d) return '';
  if (typeof d === 'string' && d.includes('T')) return d.split('T')[0];
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

// ✅ تهريب النصوص لصيغة XML آمنة
const escapeXml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

// ✅ بناء بيانات الهيدر (Header) من بوليصة (سواء snake_case أو camelCase)
const buildHeaderFromBol = (bol = {}) => {
  return {
    manifestNo: bol.manifest_no || bol.manifestNo || null,
    customsOfficeCode: bol.customs_office_code || bol.customsOfficeCode || '',
    voyageNumber: bol.voyage_number || bol.voyageNumber || bol.voyage_flight_no || bol.voyageFlightNo || '',
    flightNo: bol.flight_no || bol.flightNo || bol.voyage_flight_no || bol.voyageFlightNo || '',
    ownerOperator: bol.owner_operator || bol.ownerOperator || '',
    registration: bol.aircraft_registration || bol.registration || '',
    dateOfDeparture: bol.date_of_departure || bol.dateOfDeparture || null,
    dateOfArrival: bol.date_of_arrival || bol.dateOfArrival || null,
    placeOfDepartureCode: bol.port_of_loading || bol.place_of_loading_code || bol.placeOfLoadingCode || bol.placeOfDepartureCode || '',
    placeOfDestinationCode: bol.port_of_discharge || bol.place_of_unloading_code || bol.placeOfUnloadingCode || bol.placeOfDestinationCode || '',
    etd: bol.etd || null,
    carrierName: bol.carrier_name || bol.carrierName || '',
    carrierAddress: bol.carrier_address || bol.carrierAddress || '',
    shippingAgentCode: bol.shipping_agent_code || bol.shippingAgentCode || '',
    shippingAgentName: bol.shipping_agent_name || bol.shippingAgentName || '',
    modeOfTransportCode: bol.mode_of_transport_code || bol.modeOfTransportCode || '4',
    transporterNationality: bol.transporter_nationality || bol.transporterNationality || 'SD',
  };
};

// ✅ تنزيل ملف XML للمستخدم
const downloadXmlFile = (xmlContent, fileName) => {
  const blob = new Blob([xmlContent], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};


// ✅ دالة لملء بيانات البوليصة من الرحلات (voyages) أو الحجوزات (reservations)
const hydrateBolWithVoyageData = (bol, voyagesList, reservationsList) => {
  if (!bol) return bol;
  const voyage = voyagesList.find(v =>
    v.voyage_number === bol.voyage_number ||
    v.id === bol.voyage_id
  );
  const reservation = reservationsList.find(r => r.id === bol.reservation_id);
  return {
    ...bol,
    owner_operator: bol.owner_operator || voyage?.owner_operator || reservation?.owner_operator || null,
    aircraft_registration: bol.aircraft_registration || bol.registration || voyage?.aircraft_registration || voyage?.registration || reservation?.aircraft_registration || reservation?.registration || null,
    registration: bol.registration || bol.aircraft_registration || voyage?.registration || voyage?.aircraft_registration || reservation?.registration || reservation?.aircraft_registration || null,
    flight_no: bol.flight_no || voyage?.flight_no || voyage?.voyage_number || reservation?.flight_no || null,
    voyage_number: bol.voyage_number || voyage?.voyage_number || reservation?.voyage_number || null,
    customs_office_code: bol.customs_office_code || voyage?.customs_office_code || reservation?.customs_office_code || null,
    port_of_loading: bol.port_of_loading || bol.place_of_loading_code || voyage?.port_of_loading || voyage?.place_of_loading_code || voyage?.placeOfDepartureCode || reservation?.port_of_loading || reservation?.place_of_loading_code || null,
    port_of_discharge: bol.port_of_discharge || bol.place_of_unloading_code || voyage?.port_of_discharge || voyage?.place_of_unloading_code || voyage?.placeOfDestinationCode || reservation?.port_of_discharge || reservation?.place_of_unloading_code || null,
    place_of_loading_code: bol.place_of_loading_code || bol.port_of_loading || voyage?.place_of_loading_code || voyage?.port_of_loading || voyage?.placeOfDepartureCode || reservation?.place_of_loading_code || reservation?.port_of_loading || null,
    place_of_unloading_code: bol.place_of_unloading_code || bol.port_of_discharge || voyage?.place_of_unloading_code || voyage?.port_of_discharge || voyage?.placeOfDestinationCode || reservation?.place_of_unloading_code || reservation?.port_of_discharge || null,
    date_of_departure: bol.date_of_departure || voyage?.date_of_departure || voyage?.dateOfDeparture || reservation?.date_of_departure || null,
    date_of_arrival: bol.date_of_arrival || voyage?.date_of_arrival || voyage?.dateOfArrival || reservation?.date_of_arrival || null,
    etd: bol.etd || voyage?.etd || reservation?.etd || null,
  };
};

// ✅ حساب المجاميع (مطابق لـ ManifestScreen)
const computeTotalsFromBols = (masters) => {
  let totalBols = 0;
  let totalPackages = 0;
  let totalContainers = 0;
  let totalGrossMass = 0;
  (masters || []).forEach((m) => {
    if (m.is_offloaded) return;
    if (Array.isArray(m.homes) && m.homes.length > 0) {
      m.homes.forEach((h) => {
        if (h.is_offloaded) return;
        totalBols++;
        totalPackages += Number(h.numberOfPackages || h.number_of_packages || 0);
        totalContainers += Number(h.numOfCtnForThisBol || h.num_of_ctn_for_this_bol || 0);
        totalGrossMass += Number(h.grossMass || h.gross_mass || 0);
      });
    } else {
      totalBols++;
      totalPackages += Number(m.numberOfPackages || m.number_of_packages || 0);
      totalContainers += Number(m.numOfCtnForThisBol || m.num_of_ctn_for_this_bol || 0);
      totalGrossMass += Number(m.grossMass || m.gross_mass || 0);
    }
  });
  return {
    totalBols: String(totalBols),
    totalPackages: String(totalPackages),
    totalContainers: String(totalContainers),
    totalGrossMass: String(totalGrossMass),
  };
};

const buildManifestGeneralFromBol = (bol, currentForm) => {
  const header = buildHeaderFromBol(bol);
  const totals = computeTotalsFromBols([bol]);
  return {
    ...header,
    manifestNo: currentForm.manifestNo || header.manifestNo || null,
    customsOfficeCode: bol.customs_office_code || bol.customsOfficeCode || currentForm.customsOfficeCode || header.customsOfficeCode || '',
    voyageNumber: header.voyageNumber || bol.voyage_number || bol.voyage_flight_no || currentForm.voyageFlightNo || '',
    dateOfDeparture: header.dateOfDeparture || bol.date_of_departure || bol.dateOfDeparture || currentForm.dateOfDeparture || null,
    dateOfArrival: header.dateOfArrival || bol.date_of_arrival || bol.dateOfArrival || currentForm.storageEndDate || null,
    placeOfDepartureCode: header.placeOfDepartureCode || bol.port_of_loading || bol.place_of_loading_code || bol.placeOfLoadingCode || '',
    placeOfDestinationCode: header.placeOfDestinationCode || bol.port_of_discharge || bol.place_of_unloading_code || bol.placeOfUnloadingCode || '',
    ownerOperator: header.ownerOperator || bol.owner_operator || bol.ownerOperator || '',
    registration: header.registration || bol.aircraft_registration || bol.registration || '',
    flightNo: header.flightNo || bol.flight_no || bol.voyage_flight_no || '',
    etd: header.etd || bol.etd || null,
    totalBols: totals.totalBols,
    totalPackages: totals.totalPackages,
    totalContainers: totals.totalContainers,
    totalGrossMass: totals.totalGrossMass,
    declaredTotalBols: totals.totalBols,
  };
};

// const buildManifestPayloadFromBol = (bol, currentForm = null, voyagesList, reservationsList) => {
//   const hydratedBol = hydrateBolWithVoyageData(bol, voyagesList, reservationsList);
//   const effectiveForm = currentForm || hydratedBol;
//   const general = buildManifestGeneralFromBol(hydratedBol, effectiveForm);
//   return {
//     general,
//     bols: [hydratedBol]
//   };
// };

// ✅ تحقق تواريخ التخزين: يتأكد أن storageEndDate >= storageStartDate لكل بوليصة وهاوس
const validateStorageDates = (bol, currentForm) => {
  const parse = (d, fallback) => {
    if (!d && !fallback) return null;
    const v = d || fallback;
    if (!v) return null;

    const normalized = typeof v === 'string' && v.includes('T') ? v.split('T')[0] : String(v);
    if (!normalized || normalized.startsWith('0000-00-00')) return null;

    const date = new Date(normalized);
    if (isNaN(date.getTime())) return null;
    if (date.getFullYear() < 1901) return null;
    return date;
  };

  const start = parse(bol.storage_start_date || bol.storageStartDate, currentForm.storageStartDate);
  const end = parse(
    bol.storage_end_date || bol.storageEndDate,
    currentForm.storageEndDate || bol.date_of_departure || bol.dateOfDeparture || currentForm.dateOfDeparture
  );
  if (start && end && end < start) {
    return { valid: false, message: 'تاريخ نهاية التخزين أصغر من تاريخ البداية في البوليصة ' + (bol.bol_reference || bol.bolReference || '') };
  }

  // تحقق للهاوسات إن وجدت
  if (Array.isArray(bol.homes)) {
    for (const h of bol.homes) {
      const hs = parse(h.storageStartDate || h.storage_start_date, bol.storageStartDate || currentForm.storageStartDate);
      const he = parse(
        h.storageEndDate || h.storage_end_date,
        bol.storageEndDate || currentForm.storageEndDate || h.dateOfDeparture || h.date_of_departure || bol.dateOfDeparture || bol.date_of_departure
      );
      if (hs && he && he < hs) {
        return { valid: false, message: 'تاريخ نهاية التخزين أصغر من تاريخ البداية في هاوس البوليصة ' + (h.bolReference || h.bol_reference || '') };
      }
    }
  }

  return { valid: true };
};

// const saveManifestForBol = async (
//     bol,
//     currentForm,
//     manifestMapObj,
//     setManifestMapFn,
//     fetchBolsFn,
//     fetchManifestsFn,
//     voyagesList,
//     reservationsList,
//     selectedManifestIdArg = null
//   ) => {
//     if (!bol) return null;
//     const manifestKey = selectedManifestIdArg
//       ? `manifest-${selectedManifestIdArg}`
//       : currentForm?.manifestNo
//         ? `manifestNo-${String(currentForm.manifestNo).trim()}`
//         : null;

//     const key = manifestKey || ((bol.id !== undefined && bol.id !== null) ? `bol-${bol.id}` : (bol.bol_reference || bol.bolReference || null));
//     if (!key) return null;
//     if (manifestMapObj[key]) return manifestMapObj[key];

//     const payload = buildManifestPayloadFromBol(bol, currentForm, voyagesList, reservationsList);
//     let headerId = null;

//     if (selectedManifestIdArg) {
//       const existingManifest = await api.get(`/manifests/${selectedManifestIdArg}/full`);
//       const combinedBols = Array.isArray(existingManifest.bols) ? [...existingManifest.bols] : [];
//       const incomingBolRef = payload.bols[0]?.bolReference || payload.bols[0]?.bol_reference;
//       const alreadyExists = combinedBols.some((b) =>
//         String(b.bolReference || b.bol_reference || '').trim() === String(incomingBolRef || '').trim()
//       );
//       if (!alreadyExists) {
//         combinedBols.push(payload.bols[0]);
//       }

//       const updatePayload = {
//         general: payload.general,
//         bols: combinedBols
//       };
//       await api.put(`/manifests/${selectedManifestIdArg}`, updatePayload);
//       headerId = selectedManifestIdArg;
//     } else {
//       const response = await api.post('/manifest', payload);
//       headerId = response?.headerId || response?.id || null;
//     }

//     if (headerId) {
//       setManifestMapFn((prev) => ({ ...prev, [key]: headerId }));
//       if (fetchBolsFn) await fetchBolsFn();
//       if (fetchManifestsFn) await fetchManifestsFn();
//     }

//     return headerId;
//   };

// ✅ توليد XML لبوليصة واحدة
function bolToAwmdsXml(bol, header = {}) {
  const g = {
    customsOfficeCode: header.customsOfficeCode || bol.customs_office_code || "",
    voyageNumber: header.voyageNumber || bol.voyage_number || bol.voyageNumber || "",
    dateOfDeparture: toDateInput(header.dateOfDeparture || bol.date_of_departure || bol.dateOfDeparture),
    dateOfArrival: toDateInput(header.dateOfArrival || bol.date_of_arrival || bol.dateOfArrival),
    totalBols: "1",
    totalPackages: String(bol.number_of_packages || bol.numberOfPackages || 0),
    totalContainers: String(bol.num_of_ctn_for_this_bol || bol.numOfCtnForThisBol || 0),
    totalGrossMass: String(bol.gross_mass || bol.grossMass || 0),
    carrierName: header.carrierName || bol.carrier_name || "",
    carrierAddress: header.carrierAddress || bol.carrier_address || "",
    shippingAgentCode: header.shippingAgentCode || bol.shipping_agent_code || "",
    shippingAgentName: header.shippingAgentName || bol.shipping_agent_name || "",
    modeOfTransportCode: header.modeOfTransportCode || bol.mode_of_transport_code || "4",
    transporterNationality: header.transporterNationality || bol.transporter_nationality || "SD",
    placeOfDepartureCode: header.placeOfDepartureCode || bol.place_of_loading_code || bol.placeOfLoadingCode || "",
    placeOfDestinationCode: header.placeOfDestinationCode || bol.place_of_unloading_code || bol.placeOfUnloadingCode || "",
    storageStartDate: toDateInput(bol.storage_start_date || bol.storageStartDate),
    storageEndDate: toDateInput(bol.storage_end_date || bol.storageEndDate),
    storageDays: String(bol.storage_days || 0),
    storageRate: String(bol.storage_rate || 0),
    totalServiceCost: String(bol.fees?.service || 0),
    totalStorageCharge: String(bol.fees?.storage || 0),
    totalInvoice: String(bol.fees?.total || 0),
  };

  let xml = `<?xml version='1.0' encoding='utf-8'?>`;
  xml += `
<Awmds>
`;
  xml += `  <General_segment>
`;
  xml += `    <General_segment_id>
`;
  xml += `      <Customs_office_code>${escapeXml(g.customsOfficeCode)}</Customs_office_code>
`;
  xml += `      <Voyage_number>${escapeXml(g.voyageNumber)}</Voyage_number>
`;
  xml += `      <Date_of_departure>${escapeXml(g.dateOfArrival)}</Date_of_departure>
`;
  xml += `      <Date_of_arrival>${escapeXml(g.dateOfDeparture || "")}</Date_of_arrival>
`;
  xml += `    </General_segment_id>
`;
  xml += `    <Totals_segment>
`;
  xml += `      <Total_number_of_bols>${escapeXml(g.totalBols)}</Total_number_of_bols>
`;
  xml += `      <Total_number_of_packages>${escapeXml(g.totalPackages)}</Total_number_of_packages>
`;
  xml += `      <Total_number_of_containers>${escapeXml(g.totalContainers)}</Total_number_of_containers>
`;
  xml += `      <Total_gross_mass>${escapeXml(g.totalGrossMass)}</Total_gross_mass>
`;
  xml += `    </Totals_segment>
`;
  xml += `    <Transport_information>
`;
  xml += `      <Carrier>
`;
  xml += `        <Carrier_code>${escapeXml(g.carrierCode || "00123")}</Carrier_code>
`;
  xml += `        <Carrier_name>${escapeXml(g.carrierName)}</Carrier_name>
`;
  xml += `        <Carrier_address>${escapeXml(g.carrierAddress)}</Carrier_address>
`;
  xml += `      </Carrier>
`;
  xml += `      <Shipping_Agent>
`;
  xml += `        <Shipping_Agent_code>${escapeXml(g.shippingAgentCode)}</Shipping_Agent_code>
`;
  xml += `        <Shipping_Agent_name>${escapeXml(g.shippingAgentName)}</Shipping_Agent_name>
`;
  xml += `      </Shipping_Agent>
`;
  xml += `      <Mode_of_transport_code>${escapeXml(g.modeOfTransportCode)}</Mode_of_transport_code>
`;
  xml += `      <Identity_of_transporter>${escapeXml(g.identityOfTransporter || "")}</Identity_of_transporter>
`;
  xml += `      <Nationality_of_transporter_code>${escapeXml(g.transporterNationality)}</Nationality_of_transporter_code>
`;
  xml += `    </Transport_information>
`;
  xml += `    <Load_unload_place>
`;
  xml += `      <Place_of_departure_code>${escapeXml(g.placeOfDepartureCode)}</Place_of_departure_code>
`;
  xml += `      <Place_of_destination_code>${escapeXml(g.placeOfDestinationCode)}</Place_of_destination_code>
`;
  xml += `    </Load_unload_place>
`;
  xml += `    <Storage_information>
`;
  xml += `      <Storage_start_date>${escapeXml(g.storageStartDate)}</Storage_start_date>
`;
  xml += `      <Storage_end_date>${escapeXml(g.storageEndDate)}</Storage_end_date>
`;
  xml += `      <Storage_days>${escapeXml(g.storageDays)}</Storage_days>
`;
  xml += `      <Storage_rate>${escapeXml(g.storageRate)}</Storage_rate>
`;
  xml += `      <Total_storage_charge>${escapeXml(g.totalStorageCharge)}</Total_storage_charge>
`;
  xml += `      <Total_service_cost>${escapeXml(g.totalServiceCost)}</Total_service_cost>
`;
  xml += `      <Total_invoice>${escapeXml(g.totalInvoice)}</Total_invoice>
`;
  xml += `    </Storage_information>
`;
  xml += `  </General_segment>
`;

  const flattenBols = (masters) => {
    const res = [];
    (masters || []).forEach((m) => {
      res.push(m);
      if (Array.isArray(m.homes)) {
        m.homes.forEach((h) => res.push(h));
      }
    });
    return res;
  };

  const bols = flattenBols([bol]);
  let lineCounter = 1;
  bols.forEach((b) => {
    xml += `  <Bol_segment>
`;
    xml += `    <Bol_id>
`;
    xml += `      <Bol_reference>${escapeXml(b.bol_reference || b.bolReference)}</Bol_reference>
`;
    xml += `      <Line_number>${lineCounter++}</Line_number>
`;
    xml += `      <Bol_nature>${escapeXml(b.bol_nature || b.bolNature || "23")}</Bol_nature>
`;
    xml += `      <Bol_type_code>${escapeXml(b.bol_type_code || b.bolTypeCode || "740")}</Bol_type_code>
`;
    xml += `    </Bol_id>
`;
    xml += `    <Load_unload_place>
`;
    xml += `      <Place_of_loading_code>${escapeXml(b.place_of_loading_code || b.placeOfLoadingCode || g.placeOfDepartureCode)}</Place_of_loading_code>
`;
    xml += `      <Place_of_unloading_code>${escapeXml(b.place_of_unloading_code || b.placeOfUnloadingCode || g.placeOfDestinationCode)}</Place_of_unloading_code>
`;
    xml += `    </Load_unload_place>
`;
    xml += `    <Traders_segment>
`;
    xml += `      <Exporter>
`;
    xml += `        <Exporter_name>${escapeXml(b.exporter_name || b.exporterName || "")}</Exporter_name>
`;
    xml += `        <Exporter_address>${escapeXml(b.exporter_address || b.exporterAddress || "")}</Exporter_address>
`;
    xml += `      </Exporter>
`;
    xml += `      <Service_segment>
`;
    xml += `        <Service_id>${escapeXml(b.service_id || b.serviceId || "")}</Service_id>
`;
    xml += `      </Service_segment>
`;
    xml += `      <Notify>
`;
    xml += `        <Notify_name>${escapeXml(b.notify_name || b.notifyName || "")}</Notify_name>
`;
    xml += `        <Notify_address>${escapeXml(b.notify_address || b.notifyAddress || "")}</Notify_address>
`;
    xml += `      </Notify>
`;
    xml += `      <Consignee>
`;
    xml += `        <Consignee_name>${escapeXml(b.consignee_name || b.consigneeName || "")}</Consignee_name>
`;
    xml += `        <Consignee_address>${escapeXml(b.consignee_address || b.consigneeAddress || "")}</Consignee_address>
`;
    xml += `      </Consignee>
`;
    xml += `    </Traders_segment>
`;
    xml += `    <Goods_segment>
`;
    xml += `      <Number_of_packages>${escapeXml(b.number_of_packages || b.numberOfPackages || "")}</Number_of_packages>
`;
    xml += `      <Package_type_code>${escapeXml(b.package_type_code || b.packageTypeCode || "")}</Package_type_code>
`;
    xml += `      <Gross_mass>${escapeXml(b.gross_mass || b.grossMass || "")}</Gross_mass>
`;
    xml += `      <Shipping_marks>${escapeXml(b.shipping_marks || b.shippingMarks || "")}</Shipping_marks>
`;
    xml += `      <Goods_description>${escapeXml(b.goods_description || b.goodsDescription || "")}</Goods_description>
`;
    xml += `      <Num_of_ctn_for_this_bol>${escapeXml(b.num_of_ctn_for_this_bol || b.numOfCtnForThisBol || "0")}</Num_of_ctn_for_this_bol>
`;
    xml += `    </Goods_segment>
`;
    xml += `    <Value_segment></Value_segment>
`;
    xml += `  </Bol_segment>
`;
  });

  xml += `</Awmds>
`;
  return xml;
}

const BOLIssuanceScreen = ({ permissions = [] }) => {
  const today = new Date().toISOString().split('T')[0];
  const canSubmitOperator =
    hasPermission(permissions, 'OPERATOR_SUBMIT') ||
    hasPermission(permissions, 'operatorForm') ||
    hasPermission(permissions, 'MANIFEST_ENTRY');
  const canEditServices =
    hasPermission(permissions, 'SERVICES_EDIT') ||
    hasPermission(permissions, 'USERS_ADMIN') ||
    hasPermission(permissions, 'MANIFEST_ENTRY');
  const isOperatorOnly = canSubmitOperator && !canEditServices;
  const emptyForm = {
    bolReference: '',
    bolNature: '',
    bolTypeCode: '',
    notifyName: '',
    notifyAddress: '',
    customerName: '',
    numberOfPackages: '',
    packageTypeCode: '',
    grossMass: '',
    weight: '',
    volByMc: '',
    shippingMarks: '',
    goodsDescription: '',
    cargoTypes: [],
    services: [],
    packingItems: [],
    buildUpItems: [],
    cargoType: '',
    serviceType: '',
    serviceId: null,
    packingItemId: null,
    buildUpItemId: null,
    storageStartDate: today,
    storageEndDate: '',
    storageBaseAmount: '',
    storageFinalAmount: '',
    voyageFlightNo: '',
    dateOfDeparture: '',
    etd: '',
    homes: [],
    day: false,
    manifestNo: '',
    customsOfficeCode: '',
    declaredTotalAwbs: '',
    // ✅ أضف الحقول المفقودة لتفريغ كامل
    ownerOperator: '',
    registration: '',
    placeOfDepartureCode: '',
    placeOfDestinationCode: '',
    exporterName: '',
    exporterAddress: '',
    consigneeName: '',
    consigneeAddress: '',
    numOfCtnForThisBol: '',
  };

  const [reservations, setReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [isDirectIssuance, setIsDirectIssuance] = useState(false);
  const [editingBol, setEditingBol] = useState(null);
  const [bols, setBols] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [servicesList, setServicesList] = useState([]);
  const [cargoTypes, setCargoTypes] = useState([]);
  const [packingItems, setPackingItems] = useState([]);
  const [buildUpItems, setBuildUpItems] = useState([]);
  const [calculatedFees, setCalculatedFees] = useState({ service: 0, packing: 0, buildUp: 0, total: 0 });
  const [voyages, setVoyages] = useState([]);
  const [selectedVoyage, setSelectedVoyage] = useState(null);
  const [manifestBols, setManifestBols] = useState([]);
  const [manifestMap, setManifestMap] = useState({});
  const [manifests, setManifests] = useState([]);
  const [selectedManifestId, setSelectedManifestId] = useState(null);
  const [storageItems, setStorageItems] = useState([]);
  const [freePeriods, setFreePeriods] = useState([]);
  const [showMasterStorageDetails, setShowMasterStorageDetails] = useState(false);
  const [showHousesStorageDetails, setShowHousesStorageDetails] = useState(false);
  const [printOverrideModal, setPrintOverrideModal] = useState({ open: false, mode: 'receipt', bol: null, currency: 'USD', baseAmount: '', finalAmount: '' });
  const [showIssuedSection, setShowIssuedSection] = useState(true);
  const [masters, setMasters] = useState([]); // saved masters before bulk submit

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [savingManifest, setSavingManifest] = useState(false);
  const [manifestSaved, setManifestSaved] = useState(false);
  const [printCurrency, setPrintCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(2500);

  const fetchExchangeRate = async () => {
    try {
      const res = await api.get('/services/exchange-rate');
      if (res && res.rate) {
        setExchangeRate(Number(res.rate));
      }
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
    }
  };

  useEffect(() => {
    if (isOperatorOnly && !editingBol && !selectedReservation) {
      setIsDirectIssuance(true);
    }
  }, [isOperatorOnly, editingBol, selectedReservation]);

  const getDeclaredTotalAwbs = (sourceForm) => {
    const homesCount = Array.isArray(sourceForm?.homes) ? sourceForm.homes.length : 0;
    return homesCount > 0 ? homesCount + 1 : 1;
  };

  const resolveSelectedCargoType = (sourceForm) => {
    const direct = String(sourceForm?.cargoType || '').trim();
    if (direct) return direct;
    const firstFromList = Array.isArray(sourceForm?.cargoTypes) ? String(sourceForm.cargoTypes[0] || '').trim() : '';
    return firstFromList || '';
  };

  const buildOperatorSubmissionPayload = (sourceForm) => ({
    owner_operator: sourceForm.ownerOperator,
    aircraft_registration: sourceForm.registration,
    declared_total_awbs: getDeclaredTotalAwbs(sourceForm),
    customs_office_code: sourceForm.customsOfficeCode,
    voyage_flight_no: sourceForm.voyageFlightNo,
    date_of_departure: sourceForm.dateOfDeparture || null,
    etd: sourceForm.etd || null,
    port_of_loading: sourceForm.placeOfDepartureCode,
    port_of_discharge: sourceForm.placeOfDestinationCode,
    bol_reference: sourceForm.bolReference,
    bol_nature: sourceForm.bolNature || '23',
    bol_type_code: sourceForm.homes && sourceForm.homes.length > 0 ? '741' : '740',
    customer_name: sourceForm.customerName || null,
    number_of_packages: sourceForm.numberOfPackages,
    package_type_code: sourceForm.packageTypeCode || 'PK',
    gross_mass: sourceForm.grossMass,
    weight: sourceForm.weight,
    vol_by_mc: sourceForm.volByMc,
    shipping_marks: sourceForm.shippingMarks || null,
    goods_description: sourceForm.goodsDescription || null,
    cargo_types: sourceForm.cargoTypes || [],
    service_ids: (sourceForm.services || []).map((service) => service.id),
    packing_item_ids: (sourceForm.packingItems || []).map((item) => item.id),
    build_up_item_ids: (sourceForm.buildUpItems || []).map((item) => item.id),
    status: 'pending_services',
    homes: (sourceForm.homes || []).map((house) => ({
      bol_reference: house.bolReference,
      bol_nature: house.bolNature || '23',
      bol_type_code: '740',
      number_of_packages: house.numberOfPackages,
      package_type_code: house.packageTypeCode || sourceForm.packageTypeCode || 'PK',
      gross_mass: house.grossMass,
      weight: house.weight,
      vol_by_mc: house.volByMc,
      shipping_marks: house.shippingMarks || null,
      goods_description: house.goodsDescription || null,
      cargo_types: house.cargoTypes || [],
      service_ids: (house.services || []).map((service) => service.id),
      packing_item_ids: (house.packingItems || []).map((item) => item.id),
      build_up_item_ids: (house.buildUpItems || []).map((item) => item.id),
      date_of_departure: house.dateOfDeparture || sourceForm.dateOfDeparture || null,
      etd: house.etd || sourceForm.etd || null,
    })),
  });

  const validateOperatorForm = (sourceForm) => {
    if (!sourceForm.ownerOperator) return 'Please enter Owner / Operator';
    if (!sourceForm.registration) return 'Please enter Aircraft Registration';
    if (!sourceForm.placeOfDepartureCode) return 'Please enter Port of Loading';
    if (!sourceForm.placeOfDestinationCode) return 'Please enter Port of Discharge';
    if (!sourceForm.bolReference) return 'Please enter AWB Reference';
    if (!sourceForm.numberOfPackages) return 'Please enter Number of Packages';
    if (!sourceForm.grossMass && !sourceForm.weight) return 'Please enter Gross Mass or Weight';
    return null;
  };

  const handleOperatorSave = async () => {
    const validationError = validateOperatorForm(form);
    if (validationError) {
      alert(validationError);
      return;
    }

    const payload = buildOperatorSubmissionPayload(form);

    try {
      setSavingManifest(true);
      if (editingBol?.id) {
        await api.put(`/bols/${editingBol.id}`, {
          ...payload,
          returned_for_review: 0,
        });
        toast.success('✅ AWB updated and sent back to services');
      } else {
        await api.post('/bols/operator-submission', payload);
        toast.success('✅ AWB submitted successfully');
      }

      if (canEditServices) {
        await fetchBols();
      }
      setForm(emptyForm);
      setEditingBol(null);
      setSelectedReservation(null);
      setIsDirectIssuance(true);
    } catch (err) {
      console.error('Error submitting operator AWB:', err);
      alert(err?.message || 'Error submitting AWB');
    } finally {
      setSavingManifest(false);
    }
  };


  const handleSaveManifest = async () => {
    if (!canEditServices) {
      await handleOperatorSave();
      return;
    }

    if (!form.bolReference) {
      alert('❌ AWB Reference مطلوب');
      return;
    }
    const selectedCargoType = resolveSelectedCargoType(form);
    if (!selectedCargoType) {
      alert('❌ Cargo Type مطلوب');
      return;
    }

    const isUpdate = !!selectedManifestId;
    setSavingManifest(true);
    setSaveMessage(null);

    try {
      const normalizedForm = {
        ...form,
        cargoType: selectedCargoType,
      };

      // استخدم مسار الحفظ الموحد حتى تبقى جميع الحقول الجديدة متطابقة
      // مع Save This Master -> Submit All Masters.
      const result = await saveToManifestDirect(normalizedForm, editingBol);
      if (isUpdate) {
        toast.success("✅ Manifest updated successfully!");
      } else {
        toast.success(`✅ تم الحفظ بنجاح! Master ID: ${result.headerId}`);
      }

      // ✅ الحفظ موحد الآن: saveToManifestDirect (PUT /manifest/:id) يحدّث manifest_bols و bols داخل transaction واحدة.

      // ✅ بعد الحفظ/التعديل: أفرغ النموذج وحدّث القوائم
      setManifestSaved(true);
      setTimeout(() => setManifestSaved(false), 3000);

      // ✅ تحديث القوائم
      await fetchBols();
      await fetchManifests();

      // ✅ أفرغ النموذج دائماً بعد الحفظ/التحديث
      setForm(emptyForm);
      setSelectedManifestId(null);
      setEditingBol(null);
      setSelectedReservation(null);
      setIsDirectIssuance(false);

    } catch (err) {
      console.error('❌ handleSaveManifest error:', err);
      setSaveMessage({ type: 'error', text: `❌ خطأ: ${err.message}` });
      alert('❌ ' + err.message);
    } finally {
      setSavingManifest(false);
    }
  };


  // // ✅ دالة التحديث (مشابهة للحفظ لكنها ترسل PUT بدلاً من POST)
  // const handleUpdateManifest = async () => {
  //   if (!selectedManifestId) {
  //     toast.error("No manifest selected for update");
  //     return;
  //   }

  //   setUpdatingManifest(true);

  //   try {
  //     const masterCalc = buildStorageCalcFields(form);

  //     const payload = {
  //       general: {
  //         customsOfficeCode: form.customsOfficeCode || null,
  //         voyageNumber: form.voyageFlightNo || null,
  //         dateOfDeparture: form.dateOfDeparture || null,
  //         dateOfArrival: form.storageEndDate || form.dateOfDeparture || null,
  //         totalBols: form.numberOfPackages ? 1 : 0,
  //         totalPackages: form.numberOfPackages || 0,
  //         totalContainers: form.numOfCtnForThisBol || 0,
  //         totalGrossMass: form.grossMass || 0,
  //         modeOfTransportCode: '4',
  //         transporterNationality: 'SD',
  //         placeOfDepartureCode: form.placeOfDepartureCode || null,
  //         placeOfDestinationCode: form.placeOfDestinationCode || null,
  //         declaredTotalBols: form.numberOfPackages ? 1 : null,
  //         ownerOperator: form.ownerOperator || null,
  //         registration: form.registration || null,
  //         flightNo: form.voyageFlightNo || null,
  //         manifestNo: form.manifestNo || null,
  //       },
  //       bols: [{
  //         bolReference: form.bolReference,
  //         cargoType: form.cargoType || (form.cargoTypes && form.cargoTypes[0]) || 'GCR',
  //         lineNumber: 1,
  //         bolNature: form.bolNature || '23',
  //         bolTypeCode: form.homes && form.homes.length > 0 ? '741' : '740',
  //         placeOfLoadingCode: form.placeOfDepartureCode || '',
  //         placeOfUnloadingCode: form.placeOfDestinationCode || '',
  //         exporterName: form.exporterName || '',
  //         exporterAddress: form.exporterAddress || '',
  //         consigneeName: form.consigneeName || form.customerName || '',
  //         consigneeAddress: form.consigneeAddress || '',
  //         notifyName: null,
  //         notifyAddress: null,
  //         numberOfPackages: form.numberOfPackages,
  //         packageTypeCode: form.packageTypeCode,
  //         grossMass: form.grossMass,
  //         shippingMarks: form.shippingMarks,
  //         goodsDescription: form.goodsDescription,
  //         numOfCtnForThisBol: form.numOfCtnForThisBol || 0,
  //         serviceId: form.serviceId,
  //         serviceIds: (form.services || []).map(s => s.id),
  //         packingItemId: form.packingItems && form.packingItems[0] ? form.packingItems[0].id : null,
  //         packingItemIds: (form.packingItems || []).map(p => p.id),
  //         buildUpItemId: form.buildUpItems && form.buildUpItems[0] ? form.buildUpItems[0].id : null,
  //         buildUpItemIds: (form.buildUpItems || []).map(b => b.id),
  //         day: !!form.day,
  //         storageStartDate: form.storageStartDate,
  //         storageEndDate: form.storageEndDate || form.dateOfDeparture,
  //         storageFee: masterCalc.storageFee,
  //         freePeriodHours: masterCalc.freePeriodHours,
  //         storageDaysTotal: masterCalc.storageDaysTotal,
  //         billableDays: masterCalc.billableDays,
  //         homes: (form.homes || []).map((h, idx) => {
  //           const houseCalc = buildStorageCalcFields(h);
  //           return {
  //             bolReference: h.bolReference,
  //             cargoType: h.cargoType || form.cargoType || 'GCR',
  //             lineNumber: idx + 1,
  //             bolNature: h.bolNature || '23',
  //             bolTypeCode: '740',
  //             placeOfLoadingCode: form.placeOfDepartureCode || '',
  //             placeOfUnloadingCode: form.placeOfDestinationCode || '',
  //             exporterName: h.exporterName || '',
  //             exporterAddress: h.exporterAddress || '',
  //             consigneeName: h.consigneeName || '',
  //             consigneeAddress: h.consigneeAddress || '',
  //             notifyName: null,
  //             notifyAddress: null,
  //             numberOfPackages: h.numberOfPackages,
  //             packageTypeCode: h.packageTypeCode || form.packageTypeCode,
  //             grossMass: h.grossMass,
  //             shippingMarks: h.shippingMarks,
  //             goodsDescription: h.goodsDescription,
  //             numOfCtnForThisBol: h.numOfCtnForThisBol || 0,
  //             serviceId: h.serviceId,
  //             serviceIds: (h.services || []).map(s => s.id),
  //             packingItemId: h.packingItems && h.packingItems[0] ? h.packingItems[0].id : null,
  //             packingItemIds: (h.packingItems || []).map(p => p.id),
  //             buildUpItemId: h.buildUpItems && h.buildUpItems[0] ? h.buildUpItems[0].id : null,
  //             buildUpItemIds: (h.buildUpItems || []).map(b => b.id),
  //             day: !!h.day,
  //             storageStartDate: h.storageStartDate || form.storageStartDate,
  //             storageEndDate: h.storageEndDate || form.storageEndDate || form.dateOfDeparture,
  //             storageFee: houseCalc.storageFee,
  //             freePeriodHours: houseCalc.freePeriodHours,
  //             storageDaysTotal: houseCalc.storageDaysTotal,
  //             billableDays: houseCalc.billableDays,
  //           };
  //         })
  //       }]
  //     };

  //     const response = await api.put(`/manifest/${selectedManifestId}`, payload);

  //     if (!response || response.error) {
  //       throw new Error(response?.message || 'Update failed');
  //     }

  //     toast.success("✅ Manifest updated successfully!");

  //     // ✅ تفريغ الحقول بعد التحديث
  //     setForm(emptyForm);
  //     setSelectedManifestId(null);
  //     setEditingBol(null);
  //     setSelectedReservation(null);
  //     setIsDirectIssuance(false);

  //     await fetchManifests();

  //   } catch (error) {
  //     console.error("Update error:", error);
  //     toast.error("❌ " + (error.message || "Failed to update manifest"));
  //   } finally {
  //     setUpdatingManifest(false);
  //   }
  // };



  const parseJsonValue = (val) => {
    if (!val) return {};
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return {}; }
  };

  const getNumericRange = (item) => {
    const fromRaw = item?.rangeFrom ?? item?.range_from ?? item?.min_weight;
    const toRaw = item?.rangeTo ?? item?.range_to ?? item?.max_weight;
    const from = Number(fromRaw);
    const to = Number(toRaw);
    return {
      from: Number.isFinite(from) ? from : 0,
      to: Number.isFinite(to) ? to : Number.POSITIVE_INFINITY,
      hasExplicitRange: fromRaw != null || toRaw != null,
    };
  };

  const findServiceByTypeAndWeight = (type, weight) => {
    const requestedType = String(type || '').trim().toLowerCase();
    const numericWeight = Number(weight) || 0;
    const sameTypeServices = (servicesList || [])
      .filter((s) => String(s?.type || '').trim().toLowerCase() === requestedType);

    if (sameTypeServices.length === 0) return null;

    const weightedCandidates = sameTypeServices
      .filter((s) => String(s?.rate_type || s?.rateType || '').toLowerCase() !== 'per_awb')
      .map((s) => ({ service: s, ...getNumericRange(s) }))
      .sort((a, b) => {
        if (a.hasExplicitRange !== b.hasExplicitRange) return a.hasExplicitRange ? -1 : 1;
        if (a.from !== b.from) return a.from - b.from;
        return a.to - b.to;
      });

    const exact = weightedCandidates.find((c) => numericWeight >= c.from && numericWeight <= c.to);
    if (exact) return exact.service;

    const weightedFallback = weightedCandidates.find((c) => c.hasExplicitRange) || weightedCandidates[0] || null;
    if (weightedFallback?.service) return weightedFallback.service;

    // Per-AWB services do not depend on weight ranges, so pick the first matching type.
    const perAwbFallback = sameTypeServices.find(
      (s) => String(s?.rate_type || s?.rateType || '').toLowerCase() === 'per_awb'
    );
    return perAwbFallback || null;
  };

  const getChargeableWeight = (source) => {
    const actualWeight = Number(source?.grossMass ?? source?.gross_mass ?? 0) || 0;
    const volumetricWeight = Number(source?.volByMc ?? source?.vol_by_mc ?? 0) || 0;
    return Math.max(actualWeight, volumetricWeight);
  };

  const calculatePerAwbServiceFee = (services, cargoType, awbCount = 1) => {
    const priceKey = normalizeType(cargoType || 'GCR');
    return (services || []).reduce((sum, service) => {
      if (!service || service.rate_type !== 'per_awb') return sum;
      const priceSource = service.prices && typeof service.prices === 'object' ? service.prices : service;
      const rawPrice = priceSource[priceKey] ?? priceSource[priceKey.toUpperCase()] ?? priceSource.price;
      const rate = (typeof rawPrice === 'string' && rawPrice.toLowerCase() === 'free')
        ? 0
        : Number(rawPrice || 0);
      return sum + rate * awbCount;
    }, 0);
  };

  const uniqueServiceTypes = [...new Set(servicesList.map(s => s.type))];

  const handleAutoServiceSelect = (type) => {
    const weight = getChargeableWeight(form);
    const matched = findServiceByTypeAndWeight(type, weight);
    if (matched) {
      const newServices = [...form.services, matched];
      setForm(f => ({ ...f, services: newServices }));
    }
  };

  const handleHouseAutoServiceSelect = (houseIdx, type) => {
    const house = form.homes[houseIdx];
    const weight = getChargeableWeight(house);
    const matched = findServiceByTypeAndWeight(type, weight);
    if (matched) {
      const newHomes = [...form.homes];
      newHomes[houseIdx].services = [...(house.services || []), matched];
      setForm(f => ({ ...f, homes: newHomes }));
    }
  };

  const calculateDays = (start, end, isDay) => {
    if (!start || !end) return 0;
    const parseToLocal = (d) => {
      const dt = new Date(d);
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    };
    const s = parseToLocal(start);
    const e = parseToLocal(end);
    let diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
    if (isDay) diff -= 1;
    return Math.max(0, diff);
  };

  const parseFreeHours = (cargoType) => {
    const typeKey = String(cargoType || '').trim().toUpperCase();
    const row = (freePeriods || []).find((fp) => String(fp?.type || '').trim().toUpperCase() === typeKey);
    if (!row) return 0;

    const raw = row.free_period ?? row.freePeriod;
    if (raw == null || raw === '' || String(raw).toLowerCase() === 'none' || String(raw).toLowerCase() === 'no need') {
      return 0;
    }

    const match = String(raw).match(/\d+/);
    return match ? Number(match[0]) : 0;
  };

  const calculateStorageAmount = (bol, days) => {
    if (days <= 0) return 0;
    const rate = servicesList.find(s => Number(s.id) === Number(bol.service_id || bol.serviceId));
    if (!rate) return 0;
    const weight = getChargeableWeight(bol);
    return rate.isFixed ? Number(rate.price) * days : Number(rate.price) * weight * days;
  };

  const calculateStorageFee = (bol, endDateOverride) => {
    const startDate = bol.storage_start_date || bol.storageStartDate || form.storageStartDate;
    const endDate = endDateOverride ?? (bol.storage_end_date || bol.storageEndDate || form.storageEndDate);

    if (!startDate || !endDate) return { fee: 0, days: 0 };

    const totalDays = calculateDays(startDate, endDate, bol.day);
    if (totalDays <= 0) return { fee: 0, days: 0 };

    const freeHours = parseFreeHours(bol.cargo_type || bol.cargoType || "GCR");
    const totalHours = totalDays * 24;
    const billableHours = Math.max(0, totalHours - freeHours);
    const billableDays = Math.ceil(billableHours / 24);

    return {
      fee: calculateStorageAmount(bol, billableDays),
      days: billableDays,
    };
  };




  // ✅ دالة حساب عرض التخزين للتحقق قبل الإرسال
  const getStorageDisplay = (data) => {
    const persistedFee = data.storage_amount ?? data.storageFee ?? data.storage_final_amount ?? data.storageFinalAmount;
    const persistedBaseFee = data.storage_base_amount ?? data.storageBaseAmount;
    const persistedFinalFee = data.storage_final_amount ?? data.storageFinalAmount;
    const persistedBillableDays = data.billable_days ?? data.billableDays;
    const persistedTotalDays = data.storage_days_total ?? data.storageDaysTotal;

    const persistedFeeNum = Number(persistedFee);
    const persistedBillableDaysNum = Number(persistedBillableDays);
    const persistedTotalDaysNum = Number(persistedTotalDays);
    const hasPersistedFields = persistedFee != null || persistedBillableDays != null || persistedTotalDays != null;
    const hasMeaningfulPersistedValues =
      (Number.isFinite(persistedFeeNum) && persistedFeeNum > 0) ||
      (Number.isFinite(persistedBillableDaysNum) && persistedBillableDaysNum > 0) ||
      (Number.isFinite(persistedTotalDaysNum) && persistedTotalDaysNum > 0);

    if (hasPersistedFields && hasMeaningfulPersistedValues) {
      const resolved = resolveStoragePriceValues({
        baseAmount: persistedBaseFee,
        finalAmount: persistedFinalFee ?? persistedFee,
      });
      return {
        totalDays: Number(persistedTotalDaysNum || 0),
        freeHours: parseFreeHours(data.cargoType || data.cargo_type || form.cargoType || 'GCR'),
        billableDays: Number(persistedBillableDaysNum || 0),
        fee: Number(resolved.effectiveAmount || 0),
        baseFee: Number(resolved.baseAmount || 0),
        finalFee: Number(resolved.effectiveAmount || 0),
      };
    }

    const start = data.storageStartDate || data.storage_start_date || form.storageStartDate;
    const end = data.storageEndDate || data.storage_end_date || data.dateOfDeparture || form.storageEndDate || form.dateOfDeparture;
    const cargoType = data.cargoType || data.cargo_type || form.cargoType || 'GCR';
    const isDay = data.day || data.day === 1 || form.day || false;
    const weight = getChargeableWeight({
      grossMass: data.grossMass ?? data.gross_mass ?? form.grossMass,
      volByMc: data.volByMc ?? data.vol_by_mc ?? form.volByMc,
    });

    if (!start || !end) return { totalDays: 0, freeHours: 0, billableDays: 0, fee: 0 };

    const totalDays = calculateDays(start, end, isDay);
    const freeHours = parseFreeHours(cargoType);
    const totalHours = totalDays * 24;
    const billableHours = Math.max(0, totalHours - freeHours);
    const billableDays = Math.ceil(billableHours / 24);

    const cargoTypeKey = String(cargoType || '').trim().toUpperCase();
    const rate = (storageItems || []).find((s) => {
      const typeKey = String(s?.type || '').trim().toUpperCase();
      if (typeKey !== cargoTypeKey) return false;
      const from = Number(s?.rangeFrom ?? s?.range_from ?? 0);
      const toRaw = s?.rangeTo ?? s?.range_to;
      const to = Number(toRaw);
      const min = Number.isFinite(from) ? from : 0;
      const max = Number.isFinite(to) ? to : Number.POSITIVE_INFINITY;
      return weight >= min && weight <= max;
    });

    let fee = 0;
    if (rate && rate.price !== 'No need') {
      const numericPrice = Number(rate.price);
      const safePrice = Number.isFinite(numericPrice) ? numericPrice : 0;
      const isFixed = Number(rate.isFixed ?? rate.is_fixed ?? 0) === 1;
      fee = isFixed ? safePrice * billableDays : safePrice * weight * billableDays;
    }

    const resolved = resolveStoragePriceValues({
      baseAmount: fee,
      finalAmount: data.storageFinalAmount ?? data.storage_final_amount ?? data.storageFinalFee ?? null,
    });

    return { totalDays, freeHours, billableDays, fee: resolved.effectiveAmount, baseFee: resolved.baseAmount, finalFee: resolved.effectiveAmount };
  };

  // ✅ دالة موحّدة لحساب الحقول الأربعة الجديدة لأي بوليصة (ماستر أو هاوس)
  const buildStorageCalcFields = (item) => {
    const disp = getStorageDisplay(item);
    return {
      freePeriodHours: parseFreeHours(item.cargoType || item.cargo_type || form.cargoType),
      storageDaysTotal: disp.totalDays,
      billableDays: disp.billableDays,
      storageFee: disp.finalFee,
      storageBaseAmount: disp.baseFee,
      storageFinalAmount: disp.finalFee,
    };
  };

  // Live preview for UI testing: ignore persisted storage fields and recalculate from current form values.
  const getLiveStorageDisplay = (data) => getStorageDisplay({
    ...data,
    storage_amount: null,
    storageFee: null,
    storage_fee: null,
    storage_final_amount: null,
    storageFinalAmount: null,
    storage_base_amount: null,
    storageBaseAmount: null,
    billable_days: null,
    billableDays: null,
    storage_days_total: null,
    storageDaysTotal: null,
  });

  // ✅ تجميع بيانات الهاوسات لعرضها كهيدر تجميعي للماستر
  const getHousesStorageSummary = (f) => {
    const homes = Array.isArray(f.homes) ? f.homes : [];
    if (homes.length === 0) return null;
    let totalFee = 0, totalDays = 0, totalMass = 0;
    const cargoTypesSet = new Set();
    homes.forEach((h) => {
      const calc = (h.storage_amount != null || h.storageFee != null || h.billable_days != null || h.billableDays != null)
        ? {
          storageFee: Number(h.storage_amount ?? h.storageFee ?? 0),
          billableDays: Number(h.billable_days ?? h.billableDays ?? 0),
          storageDaysTotal: Number(h.storage_days_total ?? h.storageDaysTotal ?? 0),
          freePeriodHours: parseFreeHours(h.cargoType || h.cargo_type || f.cargoType),
        }
        : buildStorageCalcFields(h);
      totalFee += calc.storageFee;
      totalDays = Math.max(totalDays, calc.billableDays);
      totalMass += Number(h.grossMass || 0);
      if (h.cargoType) cargoTypesSet.add(h.cargoType);
    });
    return {
      totalFee,
      totalDays,
      totalMass,
      cargoTypes: Array.from(cargoTypesSet).join(", ") || "—",
    };
  };

  const selectVoyage = (voyage) => {
    setSelectedVoyage(voyage);
  };

  const toggleBolSelection = (bol) => {
    setManifestBols(prev =>
      prev.find(b => b.id === bol.id)
        ? prev.filter(b => b.id !== bol.id)
        : [...prev, bol]
    );
  };

  const assignBolsToManifest = () => {
    if (!selectedVoyage) {
      alert('Please select a voyage first');
      return;
    }
    setManifestBols([]);
    alert('BOLs assigned to manifest successfully');
  };

  const printLoadBreakdownSheet = (bol) => {
    const hydratedBol = hydrateBolWithVoyageData(bol, voyages, reservations);
    const v = validateStorageDates(hydratedBol, hydratedBol);
    if (!v.valid) {
      alert(v.message + '\nيرجى تصحيح تواريخ التخزين قبل الطباعة.');
      return;
    }

    const header = buildHeaderFromBol(hydratedBol);
    const printData = {
      bolReference: hydratedBol.bol_reference || hydratedBol.bolReference,
      numberOfPackages: hydratedBol.number_of_packages || hydratedBol.numberOfPackages,
      grossMass: hydratedBol.gross_mass || hydratedBol.grossMass,
      day: hydratedBol.day || false,
      cargoType: hydratedBol.cargo_type || hydratedBol.cargoType || "GCR",
      storageStartDate: hydratedBol.storage_start_date || hydratedBol.storageStartDate,
      storageEndDate: hydratedBol.storage_end_date || hydratedBol.storageEndDate,
      homes: hydratedBol.homes || [],
      ...header
    };
    generateAirCargoManifestPdf({
      header,
      bols: [printData],
      totals: computeTotalsFromBols([printData]),
      user: JSON.parse(localStorage.getItem("user") || "{}"),
      title: "LOAD BREAKDOWN SHEET",
      fileNamePrefix: "LoadBreakdownSheet"
    });
  };

  const printAirCargoManifest = (bol) => {
    const hydratedBol = hydrateBolWithVoyageData(bol, voyages, reservations);
    const v = validateStorageDates(hydratedBol, hydratedBol);
    if (!v.valid) {
      alert(v.message + '\nيرجى تصحيح تواريخ التخزين قبل الطباعة.');
      return;
    }

    const header = buildHeaderFromBol(hydratedBol);
    const printData = {
      bolReference: hydratedBol.bol_reference || hydratedBol.bolReference,
      numberOfPackages: hydratedBol.number_of_packages || hydratedBol.numberOfPackages,
      grossMass: hydratedBol.gross_mass || hydratedBol.grossMass,
      day: hydratedBol.day || false,
      cargoType: hydratedBol.cargo_type || hydratedBol.cargoType || "GCR",
      storageStartDate: hydratedBol.storage_start_date || hydratedBol.storageStartDate,
      storageEndDate: hydratedBol.storage_end_date || hydratedBol.storageEndDate,
      homes: hydratedBol.homes || [],
      ...header
    };
    generateAirCargoManifestPdf({
      header,
      bols: [printData],
      totals: computeTotalsFromBols([printData]),
      user: JSON.parse(localStorage.getItem("user") || "{}"),
      title: "AIR CARGO MANIFEST",
      fileNamePrefix: "AirCargoManifest"
    });
  };

  const printManifestDocuments = async (manifestId, type) => {
    if (!manifestId) return;
    try {
      const res = await api.get(`/manifests/${manifestId}/full`);
      const firstBol = Array.isArray(res?.bols) ? res.bols.find((b) => b && !b.parent_bol_id) || res.bols[0] : null;

      const header = {
        ...res.header,
        id: manifestId,
        manifest_no: res.header?.manifest_no || manifestId,
        manifestNo: res.header?.manifest_no || manifestId,
        registration: res.header?.registration || firstBol?.aircraft_registration_from_bols || firstBol?.aircraft_registration || firstBol?.registration || null,
        place_of_departure_code: res.header?.place_of_departure_code || firstBol?.port_of_loading_from_bols || firstBol?.port_of_loading || firstBol?.place_of_loading_code || null,
        place_of_destination_code: res.header?.place_of_destination_code || firstBol?.port_of_discharge_from_bols || firstBol?.port_of_discharge || firstBol?.place_of_unloading_code || null
      };

      const bolsToPrint = res.bols || [];
      const totals = computeTotalsFromBols(bolsToPrint);

      generateAirCargoManifestPdf({
        header,
        bols: bolsToPrint,
        totals,
        user: JSON.parse(localStorage.getItem("user") || "{}"),
        title: type === 'load_breakdown' ? 'LOAD BREAKDOWN SHEET' : 'AIR CARGO MANIFEST',
        fileNamePrefix: type === 'load_breakdown' ? `LoadBreakdownSheet_${manifestId}` : `AirCargoManifest_${manifestId}`
      });
      return true;
    } catch (err) {
      console.error('Error printing manifest:', err);
      alert('Error printing manifest document');
      return false;
    }
  };

  const handleAirCargoManifest = async (manifestId) => {
    if (!manifestId) return;

    const isReceiptedLine = (line) => {
      if (!line) return false;
      return Number(line.receipt_printed || 0) === 1 || Number(line.receipt_no || 0) > 0 || Number(line.receipt_amount || 0) > 0;
    };

    const getManifestReceiptProgress = (manifestData) => {
      const masters = Array.isArray(manifestData?.bols) ? manifestData.bols : [];
      const lines = masters.flatMap((master) => [master, ...(Array.isArray(master?.homes) ? master.homes : [])]);
      const receiptedLines = lines.filter(isReceiptedLine).length;
      return {
        totalLines: lines.length,
        receiptedLines,
        readyAll: lines.length > 0 && receiptedLines === lines.length,
      };
    };

    // شرط إلزامي: كل سطور المنفست (ماستر + هاوس) يجب أن يكون receipt_printed = 1
    try {
      console.log('[AirCargoManifest] start', { manifestId });
      const status = await api.get(`/manifests/${manifestId}/status`);
      console.log('[AirCargoManifest] status response', { manifestId, status });
      if (!status?.readyAll) {
        const manifestData = await api.get(`/manifests/${manifestId}/full`);
        const fallbackProgress = getManifestReceiptProgress(manifestData);
        if (!fallbackProgress.readyAll) {
          alert(`لا يمكن طباعة Air Cargo Manifest قبل طباعة الإيصالات لكل البوالص في المنفست (${fallbackProgress.receiptedLines}/${fallbackProgress.totalLines})`);
          return;
        }
      }

      const printed = await printManifestDocuments(manifestId, 'manifest');
      console.log('[AirCargoManifest] print result', { manifestId, printed });
      if (!printed) return;

      // بعد نجاح الطباعة فقط: تأكيد الإقلاع (is_departed=1)
      try {
        const departRes = await api.post(`/manifests/${manifestId}/mark-departed`);
        console.log('[AirCargoManifest] mark-departed response', { manifestId, departRes });
      } catch (err) {
        const msg = String(err?.message || '');
        console.error('[AirCargoManifest] mark-departed failed', { manifestId, err });
        if (!msg.includes('تم تأكيد الإقلاع مسبقاً') && !msg.includes('تم تحديث حالة المنفست إلى أقلعت')) {
          console.error('Error marking departed from Air Cargo button:', err);
          alert(msg || 'تمت الطباعة ولكن تعذر تحديث حالة الإقلاع');
          return;
        }
      }

      await fetchManifests();
      await fetchBols();
    } catch (err) {
      console.error('Error in Air Cargo Manifest flow:', err);
      alert(err?.message || 'حدث خطأ أثناء طباعة Air Cargo Manifest');
    }
  };

  const getManifestIdForBol = async (bolId) => {
    try {
      const res = await api.get(`/manifest-bols/bol/${bolId}`);
      console.log('getManifestIdForBol response:', res, 'for bolId:', bolId);
      return res || null;
    } catch (err) {
      console.error('getManifestIdForBol error:', err);
      return null;
    }
  };

  const printBolManifestDocuments = async (bol) => {
    const relation = await getManifestIdForBol(bol.id);
    const manifestId = relation?.header_id || null;
    if (!manifestId) {
      alert('تعذر تحديد المنفست المرتبط بهذه البوليصة');
      return;
    }
    await printManifestDocuments(manifestId, 'manifest');
  };

  const printStorageInvoice = async (bol, override = null) => {
    try {
      const normalizeBolRef = (value) => {
        const raw = String(value ?? '').trim();
        if (!raw) return '';
        if (/^\d+$/.test(raw)) {
          return String(Number(raw));
        }
        return raw.toUpperCase();
      };

      const requestedBolRef = String(bol?.bol_reference || bol?.bolReference || '').trim();
      const manifestContainsRef = (manifestData, ref) => {
        const targetRef = normalizeBolRef(ref);
        if (!targetRef) return false;

        const rows = Array.isArray(manifestData?.bols) ? manifestData.bols : [];
        return rows.some((row) => {
          const masterRef = normalizeBolRef(row?.bol_reference || row?.bolReference);
          if (masterRef && masterRef === targetRef) return true;

          const homes = Array.isArray(row?.homes) ? row.homes : [];
          return homes.some((house) => normalizeBolRef(house?.bol_reference || house?.bolReference) === targetRef);
        });
      };

      const resolveManifestByExactRef = async (ref) => {
        if (!ref) return null;
        const reprintData = await api.get(`/bols/for-reprint/${ref}`);
        const targetRef = normalizeBolRef(ref);
        const exactMatch = (Array.isArray(reprintData) ? reprintData : []).find((entry) => {
          const entryRef = normalizeBolRef(entry?.bol?.bol_reference || entry?.bol?.bolReference);
          return entryRef && entryRef === targetRef;
        }) || null;

        return exactMatch?.header?.id || exactMatch?.manifest_id || null;
      };

      // لا تعتمد مباشرة على selectedManifestId لأنه قد يكون منفست مختلف تم اختياره سابقاً
      let targetManifestId = bol?.manifest_id || null;
      let bolManifestRelation = null;

      // fallback 1: manifest_bols relation
      if (bol?.id) {
        bolManifestRelation = await getManifestIdForBol(bol.id);
        if (bolManifestRelation?.header_id) {
          targetManifestId = bolManifestRelation.header_id;
        }
      }

      // fallback 1.5: use selected manifest only if no reliable resolved id found
      if (!targetManifestId && selectedManifestId) {
        targetManifestId = selectedManifestId;
      }

      // fallback 2: lookup by AWB reference (returns header in for-reprint endpoint)
      if (!targetManifestId && bol?.bol_reference) {
        try {
          targetManifestId = await resolveManifestByExactRef(bol.bol_reference);
        } catch (lookupErr) {
          console.warn('Could not resolve manifest by bol_reference:', lookupErr);
        }
      }

      if (!targetManifestId) {
        alert('تعذر تحديد المنفست المرتبط بهذه البوليصة لطباعة فاتورة التخزين');
        return;
      }

      // 1) جلب المنفست الكامل
      let fullManifest;
      try {
        fullManifest = await api.get(`/manifests/${targetManifestId}/full`);
      } catch (manifestErr) {
        // في حال كان selectedManifestId قديم/خاطئ، حاول إعادة الحل عبر for-reprint
        if (bol?.bol_reference) {
          const fallbackHeaderId = await resolveManifestByExactRef(bol.bol_reference);
          if (fallbackHeaderId && Number(fallbackHeaderId) !== Number(targetManifestId)) {
            targetManifestId = fallbackHeaderId;
            fullManifest = await api.get(`/manifests/${targetManifestId}/full`);
          } else {
            throw manifestErr;
          }
        } else {
          throw manifestErr;
        }
      }

      if (requestedBolRef && !manifestContainsRef(fullManifest, requestedBolRef)) {
        const exactHeaderId = await resolveManifestByExactRef(requestedBolRef);
        if (exactHeaderId && Number(exactHeaderId) !== Number(targetManifestId)) {
          targetManifestId = exactHeaderId;
          fullManifest = await api.get(`/manifests/${targetManifestId}/full`);
        }
      }

      if (requestedBolRef && !manifestContainsRef(fullManifest, requestedBolRef)) {
        alert('تعذر مطابقة البوليصة المطلوبة مع المنفست الصحيح قبل إصدار فاتورة التخزين');
        return;
      }

      const header = fullManifest.header || {};
      const manifestBols = fullManifest.bols || [];

      // 2) تاريخ المغادرة/الانتهاء المستخدم في الطباعة فقط
      const departureDate = header.dateOfArrival || header.date_of_arrival || header.storageEndDate || header.storage_end_date;

      // ✅ لا نعيد حساب أو تحديث التخزين وقت الطباعة؛ نعتمد على القيم المحفوظة في قاعدة البيانات

      // 5) إصدار رقم فاتورة تخزين موحد من billing_documents وتحديث is_departed=1
      const issuedInvoice = await api.post(`/manifests/${targetManifestId}/storage-invoice`, {
        bolId: bol?.id,
        bolReference: bol?.bol_reference || bol?.bolReference || null,
        amountType: override?.currency || printCurrency,
      });

      // 6) إعادة جلب البيانات للطباعة فقط (بدون أي إعادة حساب/تحديث)
      const refreshed = await api.get(`/manifests/${targetManifestId}/full`);
      const targetRefNorm = normalizeBolRef(bol?.bol_reference || bol?.bolReference || bolManifestRelation?.bol_reference);
      const targetManifestBolId = Number(bolManifestRelation?.manifest_bol_id || 0);
      const targetBolId = Number(bol?.id || 0);
      console.log('refreshed.bols sample:', refreshed.bols?.[0]);

      let masterLine = (refreshed.bols || []).find((m) => {
        const mManifestBolId = Number(m?.id || 0);
        const mBolId = Number(m?.bol_id || 0);
        const mRefNorm = normalizeBolRef(m?.bol_reference ?? m?.bolReference ?? m?.reference);

        if (targetManifestBolId > 0 && mManifestBolId === targetManifestBolId) return true;
        if (targetBolId > 0 && mBolId > 0 && mBolId === targetBolId) return true;
        if (targetRefNorm && mRefNorm && targetRefNorm === mRefNorm) return true;
        return false;
      });

      // If the resolved row is a house, print using its master line to keep header-like fields (ETD, totals) stable.
      if (masterLine?.is_house && Number(masterLine.parent_bol_id || 0) > 0) {
        const parentId = Number(masterLine.parent_bol_id);
        const parentLine = (refreshed.bols || []).find((m) => Number(m?.id || 0) === parentId);
        if (parentLine) {
          masterLine = parentLine;
        }
      }

      if (!masterLine) {
        console.error('Storage invoice print match failed', {
          targetManifestId,
          targetManifestBolId,
          targetBolId,
          targetRefNorm,
          refreshedMasters: (refreshed.bols || []).map((m) => ({
            id: m?.id,
            bol_id: m?.bol_id,
            bol_reference: m?.bol_reference,
          })),
        });
        alert('تم إصدار الفاتورة، لكن تعذر جلب بيانات البوليصة للطباعة');
        await fetchManifests();
        return;
      }

      const invoiceInfo = (refreshed.bols || []).find((m) => {
        const mManifestBolId = Number(m?.id || 0);
        const mBolId = Number(m?.bol_id || 0);
        const mRefNorm = normalizeBolRef(m?.bol_reference || m?.bolReference);
        return (
          (targetManifestBolId > 0 && mManifestBolId === targetManifestBolId) ||
          (targetBolId > 0 && mBolId > 0 && mBolId === targetBolId) ||
          (targetRefNorm && mRefNorm && mRefNorm === targetRefNorm)
        );
      });

      // 8) تحضير بيانات الطباعة (نفس منطق handleTakeOff)
      const printData = { ...masterLine };
      const resolvedBaseAmount = Number(
        masterLine?.storage_base_amount ?? masterLine?.storageBaseAmount ??
        bol?.storage_base_amount ?? bol?.storageBaseAmount ?? 0
      ) || 0;
      const resolvedEffectiveAmount = Number(
        masterLine?.storage_final_amount ?? masterLine?.storageFinalAmount ??
        masterLine?.storage_amount ?? masterLine?.storageFee ?? masterLine?.storage_fee ??
        bol?.storage_final_amount ?? bol?.storageFinalAmount ??
        bol?.storage_amount ?? bol?.storageFee ?? bol?.storage_fee ?? 0
      ) || 0;

      printData.storage_fee = resolvedEffectiveAmount;
      printData.storage_amount = resolvedEffectiveAmount;
      printData.storage_final_amount = resolvedEffectiveAmount;
      printData.storage_base_amount = resolvedBaseAmount;
      printData.billable_days = masterLine.billable_days ?? masterLine.billableDays ?? 0;
      printData.storage_start_date = masterLine.storage_start_date ?? masterLine.storageStartDate;
      printData.storage_end_date = masterLine.storage_end_date ?? masterLine.storageEndDate ?? departureDate;

      if (Array.isArray(printData.homes)) {
        printData.homes = printData.homes.map((h) => ({
          ...h,
          storage_amount: resolveStoragePriceValues({
            baseAmount: h?.storage_base_amount ?? h?.storageBaseAmount,
            finalAmount: h?.storage_final_amount ?? h?.storageFinalAmount ?? h?.storage_amount ?? h?.storageFee,
          }).effectiveAmount,
          storage_fee: resolveStoragePriceValues({
            baseAmount: h?.storage_base_amount ?? h?.storageBaseAmount,
            finalAmount: h?.storage_final_amount ?? h?.storageFinalAmount ?? h?.storage_amount ?? h?.storageFee,
          }).effectiveAmount,
          storage_base_amount: resolveStoragePriceValues({
            baseAmount: h?.storage_base_amount ?? h?.storageBaseAmount,
            finalAmount: h?.storage_final_amount ?? h?.storageFinalAmount ?? h?.storage_amount ?? h?.storageFee,
          }).baseAmount,
          storage_final_amount: resolveStoragePriceValues({
            baseAmount: h?.storage_base_amount ?? h?.storageBaseAmount,
            finalAmount: h?.storage_final_amount ?? h?.storageFinalAmount ?? h?.storage_amount ?? h?.storageFee,
          }).effectiveAmount,
          billable_days: h.billable_days ?? h.billableDays ?? 0,
          storage_start_date: h.storage_start_date ?? h.storageStartDate,
          storage_end_date: h.storage_end_date ?? h.storageEndDate ?? departureDate,
          bol_reference: h.bolReference ?? h.bol_reference,
          number_of_packages: h.numberOfPackages ?? h.number_of_packages,
          gross_mass: h.grossMass ?? h.gross_mass,
          weight: h.weight ?? h.weight_kg,
          vol_by_mc: h.volByMc ?? h.vol_by_mc,
          goods_description: h.goodsDescription ?? h.goods_description,
        }));
      }

      const totalService = 0;
      const totalStorage =
        Number(printData.storage_fee) +
        (printData.homes?.reduce((sum, h) => sum + Number(h.storage_fee || 0), 0) || 0);

      // ✅ للعرض فقط: استخدم قيمة الماستر المحفوظة في DB كما هي بدون إعادة حساب
      const masterServiceFee = Number(masterLine.storage_amount ?? masterLine.storageFee ?? 0);

      // ✅ نفس منطق إعادة طباعة فاتورة التخزين (ReportsScreen) لضمان تطابق المبلغ الظاهر مع الإيصال الفعلي المحفوظ
      const savedReceiptAmount =
        printData.receipt_final_amount ??
        printData.receiptFinalAmount ??
        printData.receipt_amount ??
        printData.bd_receipt_amount ??
        printData.receiptAmount ??
        0;

      Object.assign(printData, {
        ...header,
        bol_reference: printData.bolReference ?? printData.bol_reference,
        number_of_packages: printData.numberOfPackages ?? printData.number_of_packages,
        gross_mass: printData.grossMass ?? printData.gross_mass,
        weight: printData.weight ?? printData.weight_kg,
        vol_by_mc: printData.volByMc ?? printData.vol_by_mc,
        goods_description: printData.goodsDescription ?? printData.goods_description,
        flight_no: header.flightNo ?? header.flight_no ?? bol?.flight_no ?? bol?.voyage_flight_no,
        voyage_flight_no: printData.voyage_flight_no ?? bol?.voyage_flight_no ?? header.voyage_number ?? header.flightNo ?? header.flight_no,
        aircraft_registration: printData.aircraft_registration ?? bol?.aircraft_registration ?? bol?.registration ?? header.registration ?? header.aircraft_registration,
        registration: printData.registration ?? bol?.registration ?? bol?.aircraft_registration ?? header.registration ?? header.aircraft_registration,
        port_of_loading: printData.port_of_loading ?? bol?.port_of_loading ?? bol?.place_of_loading_code ?? header.place_of_departure_code,
        port_of_discharge: printData.port_of_discharge ?? bol?.port_of_discharge ?? bol?.place_of_unloading_code ?? header.place_of_destination_code,
        etd: printData.etd ?? printData.etd_from_bols ?? masterLine?.etd ?? masterLine?.etd_from_bols ?? bol?.etd ?? header.etd,
        d_no: issuedInvoice?.invoice_no ?? invoiceInfo?.invoiceNo ?? invoiceInfo?.d_no,
        storage_end_date: departureDate,
        master_service_fee: masterServiceFee,
        fees: {
          ...parseJsonValue(bol.fees),
          storage: totalStorage,
        },
      });

      generateBolInvoicePdf({
        bol: printData,
        header,
        days: printData.billable_days,
        d_no: issuedInvoice?.invoice_no ?? invoiceInfo?.d_no,
        savedInvoiceAmount: Number(issuedInvoice?.total_amount ?? resolvedEffectiveAmount),
        savedReceiptAmount,
        masterServiceFee,
        servicesList,
        packingItems,
        buildUpItems,
        currency: override?.currency || printCurrency,
        exchangeRate: exchangeRate,
      });

      await fetchBols();
      await fetchManifests();
    } catch (err) {
      console.error('Error printing storage invoice:', err);
      alert(err.message || 'خطأ في إصدار فاتورة التخزين');
    }
  };

  // ✅ طباعة فاتورة تخزين لبوليصة هاوس واحدة فقط، باستخدام بيانات الهيدر التجميعية للماستر
  const printHouseStorageInvoice = (masterForm, house) => {
    const calc = getStorageDisplay(house);
    const printData = {
      bol_reference: house.bolReference,
      number_of_packages: house.numberOfPackages,
      gross_mass: house.grossMass,
      goods_description: house.goodsDescription,
      cargo_type: house.cargoType,
      storage_start_date: house.storageStartDate || masterForm.storageStartDate,
      storage_end_date: house.storageEndDate || masterForm.storageEndDate || masterForm.dateOfDeparture,
      storage_fee: calc.fee,
      billable_days: calc.billableDays,
      master_bol_ref: masterForm.bolReference,
      owner_operator: masterForm.ownerOperator,
      voyage_flight_no: masterForm.voyageFlightNo,
      customs_office_code: masterForm.customsOfficeCode,
      fees: { service: 0, storage: calc.fee, total: calc.fee },
    };
    generateBolInvoicePdf({
      bol: printData,
      servicesList,
      packingItems,
      buildUpItems,
    });
  };

  const handleExportBolXml = (bol) => {
    const hydratedBol = hydrateBolWithVoyageData(bol, voyages, reservations);
    const header = buildHeaderFromBol(hydratedBol);
    const xmlText = bolToAwmdsXml(hydratedBol, header);
    const fileName = `AWB_${bol.bol_reference || bol.bolReference || "NEW"}.xml`;
    downloadXmlFile(xmlText, fileName);
  };

  const fetchReservations = async () => {
    try {
      const res = await api.get('/reservations');
      setReservations(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching reservations:', err);
    }
  };

  useEffect(() => {
    fetchManifests();
  }, []);

  const deleteReservation = async (reservationId) => {
    if (!window.confirm('Are you sure you want to delete this reservation?')) return;
    try {
      await api.del(`/reservations/${reservationId}`);
      setReservations((prev) => prev.filter((r) => r.id !== reservationId));
      if (selectedReservation?.id === reservationId) {
        setSelectedReservation(null);
      }
    } catch (err) {
      console.error('Error deleting reservation:', err);
      alert('Error deleting reservation');
    }
  };

  const deleteBol = async (bolId) => {
    if (!window.confirm('Are you sure you want to delete this AWB?')) return;
    try {
      await api.del(`/bols/${bolId}`);
      setBols((prev) => prev.filter((b) => b.id !== bolId));
      if (editingBol?.id === bolId) {
        cancelEdit();
      }
    } catch (err) {
      console.error('Error deleting AWB:', err);
      alert('Error deleting AWB');
    }
  };

  const formatCargoType = (val) => {
    if (!val) return "";
    if (val.startsWith("[")) { try { return JSON.parse(val)[0]; } catch (e) { return val; } }
    return val;
  };

  const fetchBols = async () => {


    try {
      const res = await api.get('/bols');
      const mapped = Array.isArray(res)
        ? res.map((bol) => ({ ...bol, fees: parseJsonValue(bol.fees) }))
        : [];
      setBols(
        mapped
      );
      return mapped;
    } catch (err) {
      console.error('Error fetching AWBs:', err);
      return [];
    }
  };

  const fetchServices = async () => {
    try {
      const res = await api.get('/services');
      if (Array.isArray(res)) {
        const mapped = res.map(s => {
          const flattenedPrices = {};
          if (s.prices) {
            Object.keys(s.prices).forEach(key => {
              flattenedPrices[key.toLowerCase()] = s.prices[key];
            });
          }
          return { ...s, ...flattenedPrices };
        });
        setServicesList(mapped);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  };

  const fetchPacking = async () => {
    try {
      const res = await api.get('/packing');
      setPackingItems(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching packing:', err);
    }
  };

  const fetchBuildUp = async () => {
    try {
      const res = await api.get('/build-up');
      setBuildUpItems(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching build-up:', err);
    }
  };

  const fetchCargoTypes = async () => {
    try {
      const res = await api.get('/cargo-types');
      setCargoTypes(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching cargo types:', err);
    }
  };

  const fetchManifests = async () => {
    try {
      const res = await api.get('/manifests/open');
      setManifests(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching manifests:', err);
      setManifests([]);
    }
  };

  const fetchStorageData = async () => {
    try {
      const [st, fp] = await Promise.all([
        api.get('/storage'),
        api.get('/free-periods')
      ]);
      setStorageItems(Array.isArray(st) ? st.map(item => ({
        ...item,
        rangeFrom: item.range_from ?? item.rangeFrom ?? null,
        rangeTo: item.range_to ?? item.rangeTo ?? null,
        isFixed: item.is_fixed ?? item.isFixed ?? false
      })) : []);
      setFreePeriods(Array.isArray(fp) ? fp : []);
    } catch (err) {
      console.error('Error fetching storage data:', err);
    }
  };

  const getNonDepartedManifests = () => {
    return manifests.filter(m => !m.is_delivered && !m.is_offloaded);
  };

  const selectReservation = (reservation) => {
    if (!servicesList.length && !packingItems.length && !buildUpItems.length) {
      console.warn('Data not fully loaded yet');
    }

    setEditingBol(null);
    setIsDirectIssuance(false);
    setSelectedReservation(reservation);

    const cargoTypesArr = Array.isArray(reservation.cargo_type)
      ? reservation.cargo_type
      : (typeof reservation.cargo_type === 'string'
        ? JSON.parse(reservation.cargo_type || '[]')
        : []);

    const formatDate = (isoDate) => {
      if (!isoDate) return '';
      return isoDate.split('T')[0];
    };

    const initialForm = {
      ...emptyForm,
      bolReference: reservation.bol_reference || '',
      bolNature: reservation.bol_nature || '23',
      notifyName: '',
      notifyAddress: '',
      customerName: reservation.customer_name || '',
      numberOfPackages: reservation.number_of_packages || '',
      packageTypeCode: reservation.package_type_code || 'PK',
      grossMass: reservation.gross_mass || '',
      shippingMarks: reservation.shipping_marks || 'A',
      goodsDescription: reservation.goods_description || '',
      cargoTypes: parseIdArray(reservation.cargo_types),
      services: parseIdArray(reservation.service_ids).map(id => servicesList.find(s => s.id == id)).filter(Boolean),
      packingItems: parseIdArray(reservation.packing_item_ids).map(id => packingItems.find(p => p.id == id)).filter(Boolean),
      buildUpItems: parseIdArray(reservation.build_up_item_ids).map(id => buildUpItems.find(b => b.id == id)).filter(Boolean),
      cargoType: parseIdArray(reservation.cargo_types)[0] || '',
      serviceType: parseIdArray(reservation.service_ids)[0] ? servicesList.find(s => s.id == parseIdArray(reservation.service_ids)[0])?.type || '' : '',
      serviceId: parseIdArray(reservation.service_ids)[0] || null,
      bolTypeCode: reservation.bol_type_code || (reservation.homes?.length > 0 ? '741' : '740'),
      storageStartDate: today,
      storageEndDate: formatDate(reservation.date_of_departure) || '',
      homes: [],
      day: false,
      manifestNo: '',
      customsOfficeCode: ''
    };
    const initialStorageDisplay = getStorageDisplay(initialForm);
    setForm({
      ...initialForm,
      storageBaseAmount: initialStorageDisplay.baseFee,
      storageFinalAmount: initialStorageDisplay.finalFee,
    });
  };

  const editBOL = (bol) => {
    setSelectedReservation(null);
    setEditingBol(bol);

    const safeDateInput = (value) => {
      if (!value) return '';
      let normalized = '';
      if (typeof value === 'string') {
        normalized = value.includes('T') ? value.split('T')[0] : value;
      } else {
        const d = new Date(value);
        if (isNaN(d.getTime())) return '';
        normalized = d.toISOString().split('T')[0];
      }
      if (!normalized || normalized.startsWith('0000-00-00')) return '';
      const year = Number(normalized.slice(0, 4));
      if (!Number.isFinite(year) || year < 1901) return '';
      return normalized;
    };

    // ✅ NEW: تحميل المنفست المرتبط من manifest_header عبر manifest_no
    const loadManifestForEdit = async (bolRef) => {
      try {
        // استخدم for-reprint بدلاً من by-reference
        const res = await api.get(`/bols/for-reprint/${bolRef}`);
        if (res && res.length > 0) {
          const data = res[0];
          if (data.header && data.header.id) {
            setSelectedManifestId(data.header.id);
            setForm(prev => ({
              ...prev,
              manifestNo: data.header.manifest_no || String(data.header.id)
            }));
          }
        }
      } catch (e) {
        console.log('No manifest found for this BOL:', e);
        // إذا لم يُجد منفست، حاول جلب manifest_id من bols مباشرة
        try {
          const bolsRes = await api.get('/bols');
          const matched = bolsRes.find(b => b.bol_reference === bolRef);
          if (matched && matched.manifest_id) {
            setSelectedManifestId(matched.manifest_id);
            setForm(prev => ({ ...prev, manifestNo: String(matched.manifest_id) }));
          }
        } catch (e2) {
          console.log('Also not found in bols list');
        }
      }
    };
    loadManifestForEdit(bol.bol_reference);

    // ✅ NEW: تحميل المنفست المرتبط
    if (bol.manifest_id) {
      setSelectedManifestId(bol.manifest_id);
    } else if (bol.header_id) {
      setSelectedManifestId(bol.header_id);
    } else {
      setSelectedManifestId(null);
    }

    const serviceIds = parseIdArray(bol.service_ids);
    const packingIds = parseIdArray(bol.packing_item_ids);
    const buildUpIds = parseIdArray(bol.build_up_item_ids);
    const cargoTypesArr = parseIdArray(bol.cargo_types);

    const formatDate = (isoDate) => safeDateInput(isoDate);

    const servicesArr = serviceIds.length > 0
      ? serviceIds.map(id => servicesList.find(s => s.id == id)).filter(Boolean)
      : (bol.service_id ? [servicesList.find(s => s.id == bol.service_id)].filter(Boolean) : []);

    const packingArr = packingIds.length > 0
      ? packingIds.map(id => packingItems.find(p => p.id == id)).filter(Boolean)
      : (bol.packing_item_id ? [packingItems.find(p => p.id == bol.packing_item_id)].filter(Boolean) : []);

    const buildUpArr = buildUpIds.length > 0
      ? buildUpIds.map(id => buildUpItems.find(b => b.id == id)).filter(Boolean)
      : (bol.build_up_item_id ? [buildUpItems.find(b => b.id == bol.build_up_item_id)].filter(Boolean) : []);

    const formToSet = {
      ...emptyForm,
      bolReference: bol.bol_reference || '',
      bolNature: bol.bol_nature || '',
      bolTypeCode: bol.bol_type_code || '',
      customerName: bol.customer_name || bol.reservation_customer_name || '',
      notifyName: bol.notify_name || '',
      notifyAddress: bol.notify_address || '',
      numberOfPackages: bol.number_of_packages || '',
      packageTypeCode: bol.package_type_code || '',
      grossMass: bol.gross_mass || '',
      weight: bol.weight || '',
      volByMc: bol.vol_by_mc || '',
      shippingMarks: bol.shipping_marks || '',
      goodsDescription: bol.goods_description || '',
      cargoTypes: cargoTypesArr,
      services: servicesArr.map(s => ({ ...s, showPacking: false })),
      packingItems: packingArr,
      buildUpItems: buildUpArr,
      cargoType: cargoTypesArr[0] || '',
      serviceType: bol.service_type || (servicesArr[0]?.type || ''),
      serviceId: bol.service_id || (servicesArr[0]?.id || null),
      // ✅ إذا كان الماستر فارغ، ابحث في الـ Houses
      storageStartDate: formatDate(bol.storage_start_date) || today,
      storageEndDate: formatDate(bol.storage_end_date) || formatDate(bol.date_of_departure) || '',
      voyageFlightNo: bol.voyage_flight_no || (bol.homes?.[0]?.voyage_flight_no) || '',
      dateOfDeparture: formatDate(bol.date_of_departure) || formatDate(bol.homes?.[0]?.date_of_departure) || '',
      etd: bol.etd || bol.homes?.[0]?.etd || '',
      day: bol.day === 1 || bol.day === true,
      homes: Array.isArray(bol.homes) ? bol.homes.map(h => {
        const hServiceIds = parseIdArray(h.service_ids);
        const hPackingIds = parseIdArray(h.packing_item_ids);
        const hBuildUpIds = parseIdArray(h.build_up_item_ids);

        return {
          ...h,
          tempId: h.id,
          bolReference: h.bol_reference,
          grossMass: h.gross_mass,
          weight: h.weight,
          volByMc: h.vol_by_mc,
          numberOfPackages: h.number_of_packages,
          cargoType: h.cargo_type,
          goodsDescription: h.goods_description,
          services: hServiceIds.length > 0
            ? hServiceIds.map(id => servicesList.find(s => s.id == id)).filter(Boolean)
            : (h.service_id ? [servicesList.find(s => s.id == h.service_id)].filter(Boolean) : []),
          packingItems: hPackingIds.length > 0
            ? hPackingIds.map(id => packingItems.find(p => p.id == id)).filter(Boolean)
            : (h.packing_item_id ? [packingItems.find(p => p.id == h.packing_item_id)].filter(Boolean) : []),
          buildUpItems: hBuildUpIds.length > 0
            ? hBuildUpIds.map(id => buildUpItems.find(b => b.id == id)).filter(Boolean)
            : (h.build_up_item_id ? [buildUpItems.find(b => b.id == h.build_up_item_id)].filter(Boolean) : [])
        };
      }) : (bol.homes || []),
      manifestNo: bol.manifest_no || '',
      customsOfficeCode: bol.customs_office_code || '',
      declaredTotalAwbs: bol.declared_total_awbs || '',
      ownerOperator: bol.owner_operator || '',
      registration: bol.aircraft_registration || bol.registration || '',
      placeOfDepartureCode: bol.port_of_loading || bol.place_of_loading_code || '',
      placeOfDestinationCode: bol.port_of_discharge || bol.place_of_unloading_code || ''
    };
    const editStorageDisplay = getStorageDisplay(formToSet);
    setForm({
      ...formToSet,
      storageBaseAmount: editStorageDisplay.baseFee,
      storageFinalAmount: editStorageDisplay.finalFee,
    });

    const loadManifestForEditFallbacks = async () => {
      try {
        let headerId = bol.manifest_id || bol.header_id || null;
        if (!headerId && bol.bol_reference) {
          try {
            const rows = await api.get(`/bols/for-reprint/${bol.bol_reference}`);
            if (Array.isArray(rows) && rows[0]?.header?.id) {
              headerId = rows[0].header.id;
            }
          } catch {
            // Ignore and keep using existing values
          }
        }
        if (!headerId) return;

        const full = await api.get(`/manifests/${headerId}/full`);
        const manifestRows = Array.isArray(full?.bols) ? full.bols : [];
        if (manifestRows.length === 0) return;

        const ref = String(bol.bol_reference || '').trim();
        const matchedMaster = manifestRows.find((row) =>
          String(row.bol_reference || '').trim() === ref && Number(row.is_house || 0) === 0
        ) || manifestRows.find((row) => String(row.bol_reference || '').trim() === ref) || null;

        if (!matchedMaster) return;

        setSelectedManifestId(headerId);
        setForm((prev) => {
          const manifestStorageStart = formatDate(matchedMaster.storage_start_date);
          const manifestStorageEnd = formatDate(matchedMaster.storage_end_date);
          const manifestDeparture = formatDate(matchedMaster.date_of_departure);
          const bolStorageEnd = formatDate(bol.storage_end_date);
          const fallbackStorageEnd = manifestStorageEnd || manifestDeparture || bolStorageEnd || formatDate(bol.date_of_departure) || '';

          return {
            ...prev,
            customerName: prev.customerName || matchedMaster.customer_name || bol.reservation_customer_name || '',
            etd: prev.etd || matchedMaster.etd || '',
            voyageFlightNo: prev.voyageFlightNo || matchedMaster.voyage_flight_no || '',
            dateOfDeparture: prev.dateOfDeparture || formatDate(matchedMaster.date_of_departure) || '',
            storageStartDate: manifestStorageStart || prev.storageStartDate,
            storageEndDate: fallbackStorageEnd,
            manifestNo: prev.manifestNo || full?.header?.manifest_no || String(headerId),
          };
        });
      } catch (err) {
        console.warn('Could not load manifest fallbacks for edit:', err);
      }
    };

    loadManifestForEditFallbacks();
  };

  const startDirectIssuance = () => {
    setEditingBol(null);
    setSelectedReservation(null);
    setIsDirectIssuance(true);
    setForm(emptyForm);
  };

  const startNewMasterEntry = () => {
    setEditingBol(null);
    setSelectedReservation(null);
    setIsDirectIssuance(true);
    setForm(prev => ({
      ...prev,
      bolReference: '',
      bolNature: prev.bolNature || '23',
      bolTypeCode: '',
      numberOfPackages: '',
      packageTypeCode: prev.packageTypeCode || 'PK',
      grossMass: '',
      shippingMarks: '',
      goodsDescription: '',
      cargoTypes: [],
      services: [],
      packingItems: [],
      buildUpItems: [],
      cargoType: '',
      serviceType: '',
      serviceId: null,
      packingItemId: null,
      buildUpItemId: null,
      homes: [],
    }));
  };

  const clearMasterOnly = () => {
    setForm((prev) => ({
      ...prev,
      bolReference: '',
      bolNature: prev.bolNature || '23',
      bolTypeCode: '',
      numberOfPackages: '',
      packageTypeCode: prev.packageTypeCode || 'PK',
      grossMass: '',
      shippingMarks: '',
      goodsDescription: '',
      cargoTypes: [],
      services: [],
      packingItems: [],
      buildUpItems: [],
      cargoType: '',
      serviceType: '',
      serviceId: null,
      packingItemId: null,
      buildUpItemId: null,
      homes: [],
    }));
  };

  const cancelEdit = () => {
    setEditingBol(null);
    setSelectedReservation(null);
    setIsDirectIssuance(false);
    setForm(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const returnForReview = async (bolId) => {
    if (!window.confirm('Are you sure you want to return this AWB for review? It will be sent back to the Operator Application screen.')) return;
    try {
      await api.put(`/bols/${bolId}`, { status: 'operator_review' });
      await fetchBols();
      if (editingBol?.id === bolId) {
        cancelEdit();
      }
    } catch (err) {
      console.error('Error returning AWB for review:', err);
      alert('Error returning AWB for review');
    }
  };

  useEffect(() => {
    fetchServices();
    fetchPacking();
    fetchBuildUp();
    fetchCargoTypes();
    fetchExchangeRate();

    if (!isOperatorOnly) {
      fetchBols();
      fetchReservations();
      fetchStorageData(); // ✅ جديد
    }
  }, [isOperatorOnly]);

  useEffect(() => {
    const homes = Array.isArray(form.homes) ? form.homes : [];
    if (homes.length === 0) return;

    const totalPkgs = homes.reduce((sum, house) => sum + (Number(house.numberOfPackages) || 0), 0);
    const totalMass = homes.reduce((sum, house) => sum + (Number(house.grossMass) || 0), 0);
    const totalVolByMc = homes.reduce((sum, house) => sum + (Number(house.volByMc || house.vol_by_mc) || 0), 0);
    const masterChargeableWeight = Math.max(totalMass, totalVolByMc);

    if (
      String(form.numberOfPackages) !== String(totalPkgs) ||
      String(form.grossMass) !== String(totalMass) ||
      String(form.volByMc ?? '') !== String(totalVolByMc) ||
      String(form.weight) !== String(masterChargeableWeight)
    ) {
      setForm((prev) => ({
        ...prev,
        numberOfPackages: String(totalPkgs),
        grossMass: String(totalMass),
        volByMc: String(totalVolByMc),
        weight: String(masterChargeableWeight),
      }));
    }
  }, [form.homes, form.numberOfPackages, form.grossMass, form.volByMc, form.weight]);

  const selectedService = form.services.length > 0 ? form.services[0] : null;
  const normalizeType = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');
  // ✅ دالة حساب رسوم بوليصة واحدة (ماستر أو هاوس)
  const calculateSingleBolFees = (fWeight, fQty, fCargoType, fServices, fPacking, fBuildUp, fAwbCount) => {
    let sFee = 0, pFee = 0, bFee = 0;
    const priceKey = normalizeType(fCargoType || 'gcr');

    fServices.forEach(s => {
      const rate = (typeof s[priceKey] === 'string' && s[priceKey].toLowerCase() === 'free')
        ? 0
        : Number(s[priceKey] || 0);

      if (s.rate_type === 'per_kg') sFee += rate * fWeight;
      else if (s.rate_type === 'per_ton') sFee += rate * (fWeight / 1000);
      else if (s.rate_type === 'per_awb') sFee += rate * fAwbCount;
      else sFee += rate;
    });

    fPacking.forEach(p => pFee += (Number(p.price) || 0) * (Number(fQty) || 1));
    fBuildUp.forEach(b => bFee += Number(b.price) || 0);

    return { service: sFee, packing: pFee, buildUp: bFee, total: sFee + pFee + bFee };
  };

  // ✅ useEffect محسّن لحساب جميع الرسوم
  useEffect(() => {
    const quantity = Number(form.numberOfPackages) || 0;
    const weight = getChargeableWeight(form);

    // حساب رسوم الماستر
    const masterAwbMultiplier = form.homes?.length > 0 ? form.homes.length : 1;
    const masterFees = calculateSingleBolFees(
      weight, quantity, form.cargoType,
      form.services, form.packingItems, form.buildUpItems,
      masterAwbMultiplier
    );

    // حساب رسوم كل هاوس وتحديثها
    let totalHouseFees = { service: 0, packing: 0, buildUp: 0, total: 0 };
    const updatedHomes = form.homes.map(h => {
      const chargeableWeight = getChargeableWeight(h);
      const fees = calculateSingleBolFees(
        chargeableWeight, Number(h.numberOfPackages), h.cargoType,
        h.services, h.packingItems, h.buildUpItems, 1
      );
      totalHouseFees.service += fees.service;
      totalHouseFees.packing += fees.packing;
      totalHouseFees.buildUp += fees.buildUp;
      totalHouseFees.total += fees.total;
      return { ...h, fees };
    });

    // تحديث الهاوسات بالرسوم المحسوبة (للعرض)
    const feesChanged = updatedHomes.some((h, idx) => h.fees?.total !== form.homes[idx]?.fees?.total);
    if (feesChanged) {
      setForm(prev => ({ ...prev, homes: updatedHomes }));
    }

    // المجموع الكلي
    setCalculatedFees({
      service: masterFees.service + totalHouseFees.service,
      packing: masterFees.packing + totalHouseFees.packing,
      buildUp: masterFees.buildUp + totalHouseFees.buildUp,
      total: masterFees.total + totalHouseFees.total
    });
  }, [form.services, form.packingItems, form.buildUpItems, form.serviceType, form.cargoType, form.grossMass, form.volByMc, form.numberOfPackages, form.homes, servicesList, packingItems, buildUpItems]);

  useEffect(() => {
    setForm((prev) => {
      const disp = getStorageDisplay(prev);
      const nextBase = Number(disp.baseFee || 0);
      const nextFinal = prev.storageFinalAmount === '' || prev.storageFinalAmount === prev.storageBaseAmount || prev.storageFinalAmount === String(nextBase)
        ? Number(disp.finalFee || 0)
        : prev.storageFinalAmount;

      if (String(prev.storageBaseAmount ?? '') === String(nextBase) && String(prev.storageFinalAmount ?? '') === String(nextFinal)) {
        return prev;
      }

      return {
        ...prev,
        storageBaseAmount: nextBase,
        storageFinalAmount: nextFinal,
      };
    });
  }, [form.storageStartDate, form.storageEndDate, form.dateOfDeparture, form.day, form.cargoType, form.grossMass, form.volByMc, form.services, form.packingItems, form.buildUpItems, form.numberOfPackages, servicesList, storageItems]);

  useEffect(() => {
    if (!form.serviceType) return;
    const weight = getChargeableWeight(form);
    const matched = findServiceByTypeAndWeight(form.serviceType, weight);
    if (matched && matched.id != form.serviceId) {
      setForm((prev) => ({ ...prev, serviceId: matched.id }));
    }
  }, [form.serviceType, form.grossMass, form.volByMc, servicesList]);

  useEffect(() => {
    if (form.serviceId && !form.serviceType) {
      const service = servicesList.find((s) => s.id == form.serviceId);
      if (service) {
        setForm((prev) => ({ ...prev, serviceType: service.type }));
      }
    }
  }, [form.serviceId, form.serviceType, servicesList]);

  // const saveBOL = async () => {
  //   // ✅ CONSOLE LOGS للتحقق من التواريخ والحساب قبل الإرسال
  //   console.log('%c=== BOLIssuanceScreen SAVE DEBUG ===', 'color: #1976d2; font-size: 14px; font-weight: bold;');
  //   console.log('Master Start:', form.storageStartDate);
  //   console.log('Master End:', form.storageEndDate || form.dateOfDeparture);
  //   console.log('Master CargoType:', form.cargoType);
  //   console.log('Master Day:', form.day);
  //   console.log('Master Weight:', form.grossMass);


  //   const masterDisp = getStorageDisplay(form);
  //   console.log('Master Total Days:', masterDisp.totalDays);
  //   console.log('Master Free Hours:', masterDisp.freeHours);
  //   console.log('Master Billable Days:', masterDisp.billableDays);
  //   console.log('Master Storage Fee:', masterDisp.fee.toFixed(2));

  //   if (Array.isArray(form.homes)) {
  //     form.homes.forEach((h, idx) => {
  //       const houseDisp = getStorageDisplay(h);
  //       console.log(`House ${idx} Start:`, h.storageStartDate || form.storageStartDate);
  //       console.log(`House ${idx} End:`, h.storageEndDate || form.storageEndDate || form.dateOfDeparture);
  //       console.log(`House ${idx} Total Days:`, houseDisp.totalDays);
  //       console.log(`House ${idx} Billable Days:`, houseDisp.billableDays);
  //       console.log(`House ${idx} Storage Fee:`, houseDisp.fee.toFixed(2));
  //     });
  //   }
  //   console.log('%c=== END DEBUG ===', 'color: #1976d2; font-size: 14px; font-weight: bold;');

  //   let reservationId = editingBol?.reservation_id || selectedReservation?.id;
  //   const isDirect = isDirectIssuance && !reservationId;

  //   if (!form.bolReference) return alert('Please enter AWB Reference');

  //   if (isDirect) {
  //     if (!form.customerName) return alert('Please enter Customer Name');
  //     if (!form.cargoTypes || form.cargoTypes.length === 0) return alert('Please select at least one Cargo Type');
  //     if (!form.services || form.services.length === 0) return alert('Please select at least one Service');
  //     if (!form.numberOfPackages || !form.grossMass) return alert('Please fill in actual packages and weight');
  //   }

  //   const vcheck = validateStorageDates(form, form);
  //   if (!vcheck.valid) {
  //     alert(vcheck.message + '\nيرجى تصحيح تواريخ التخزين قبل الحفظ.');
  //     return;
  //   }

  //   try {
  //     if (isDirect) {
  //       const resPayload = {
  //         customerName: form.customerName,
  //         cargoTypes: form.cargoTypes,
  //         services: form.services.map(s => s.id),
  //         packingItems: form.packingItems.map(p => p.id),
  //         buildUpItems: form.buildUpItems.map(b => b.id),
  //         weight: form.grossMass,
  //         packages: form.numberOfPackages,
  //         storage_start_date: form.storageStartDate,
  //         storage_end_date: form.storageEndDate,
  //         notes: `Direct Issuance for AWB: ${form.bolReference}`
  //       };
  //       const createdRes = await api.post('/reservations', resPayload);
  //       reservationId = createdRes.id;
  //     }

  //     const derivedServiceId = form.services.length > 0
  //       ? form.services[0].id
  //       : (form.serviceId || findServiceByTypeAndWeight(form.serviceType, Number(form.grossMass))?.id || null);

  //     const normalizedHomes = (Array.isArray(form.homes) ? form.homes : [])
  //       .filter((h) => Boolean(h?.bolReference || h?.bol_reference || h?.id || h?.tempId))
  //       .map((h) => ({
  //         id: h.id || h.tempId || null,
  //         tempId: h.tempId || h.id || null,
  //         bol_reference: h.bolReference,
  //         bol_nature: h.bolNature || form.bolNature || '23',
  //         bol_type_code: h.bolTypeCode || '740',
  //         exporter_name: h.exporterName || '',
  //         exporter_address: h.exporterAddress || '',
  //         consignee_name: h.consigneeName || '',
  //         consignee_address: h.consigneeAddress || '',
  //         notify_name: h.notifyName || '',
  //         notify_address: h.notifyAddress || '',
  //         number_of_packages: h.numberOfPackages,
  //         package_type_code: h.packageTypeCode || form.packageTypeCode,
  //         gross_mass: h.grossMass,
  //         shipping_marks: h.shippingMarks || '',
  //         goods_description: h.goodsDescription || '',
  //         cargo_type: h.cargoType || form.cargoType || 'GCR',
  //         service_ids: (h.services || []).map(s => s.id),
  //         packing_item_ids: (h.packingItems || []).map(p => p.id),
  //         build_up_item_ids: (h.buildUpItems || []).map(b => b.id),
  //         day: !!h.day,
  //         date_of_departure: form.dateOfDeparture,
  //         etd: form.etd,
  //         voyage_flight_no: form.voyageFlightNo,
  // storage_start_date: h.storageStartDate || form.storageStartDate,
  //         storage_end_date: h.storageEndDate || form.storageEndDate || form.dateOfDeparture,
  //         free_period_hours: parseFreeHours(h.cargoType || form.cargoType),
  //         storage_days_total: getStorageDisplay(h).totalDays,
  //         billable_days: getStorageDisplay(h).billableDays,
  //         storage_fee: getStorageDisplay(h).fee,
  //       }));

  //     const shouldSyncHomes = !editingBol || normalizedHomes.length > 0;

  // // ✅ حساب قيم التخزين النهائية قبل الإرسال (بنفس getStorageDisplay المستخدمة في العرض)
  //     const masterStorageDisp = getStorageDisplay(form);

  //     const payload = {
  //       reservation_id: reservationId,
  //       bol_reference: form.bolReference,
  //       status: 'services_assigned',
  //       free_period_hours: parseFreeHours(form.cargoType),
  //       storage_days_total: masterStorageDisp.totalDays,
  //       billable_days: masterStorageDisp.billableDays,
  //       storage_fee: masterStorageDisp.fee,
  //       bol_nature: form.bolNature,
  //       bol_type_code: form.bolTypeCode,
  //       number_of_packages: form.numberOfPackages,
  //       package_type_code: form.packageTypeCode,
  //       gross_mass: form.grossMass,
  //       shipping_marks: form.shippingMarks,
  //       goods_description: form.goodsDescription,
  //       cargo_types: form.cargoTypes,
  //       service_ids: form.services.map(s => s.id),
  //       packing_item_ids: form.packingItems.map(p => p.id),
  //       build_up_item_ids: form.buildUpItems.map(b => b.id),
  //       ...(shouldSyncHomes ? {
  //         homes: normalizedHomes
  //       } : {}),
  //       service_id: derivedServiceId,
  //       packing_item_id: form.packingItems.length > 0 ? form.packingItems[0].id : null,
  //       build_up_item_id: form.buildUpItems.length > 0 ? form.buildUpItems[0].id : null,
  //       storage_start_date: form.storageStartDate,
  //       storage_end_date: form.storageEndDate || form.dateOfDeparture,
  //       date_of_departure: form.dateOfDeparture,
  //       etd: form.etd,
  //       voyage_flight_no: form.voyageFlightNo,
  //       day: form.day,
  //       fees: calculatedFees,
  //       manifest_no: form.manifestNo,
  //       customs_office_code: form.customsOfficeCode
  //     };

  //     const enhanced = {
  //       ...payload,
  //       fees: calculatedFees,
  //       status: 'services_assigned',
  //       service_type: form.serviceType || (form.services.length > 0 ? form.services[0].type : ''),
  //       service_name: selectedService?.name || (form.serviceType || ''),
  //       service_classification: selectedService?.classification || '',
  //       cargo_types: form.cargoTypes || [],
  //       services: form.services || [],
  //       packingItems: form.packingItems || [],
  //       buildUpItems: form.buildUpItems || [],
  //       voyageFlightNo: form.voyageFlightNo,
  //       dateOfDeparture: form.dateOfDeparture,
  //       etd: form.etd,
  //       manifest_no: form.manifestNo,
  //       customs_office_code: form.customsOfficeCode
  //     };

  //     let saved;
  //     if (editingBol) {
  //       await api.put(`/bols/${editingBol.id}`, payload);
  //       saved = { ...editingBol, ...enhanced };
  //       setBols((prev) => prev.map((item) => item.id === editingBol.id ? saved : item));
  //       alert('BOL updated successfully');
  //     } else {
  //       const created = await api.post('/bols', payload);
  //       saved = { ...created, ...enhanced };
  //       setBols([...bols, saved]);
  //       setReservations((prev) => prev.filter((r) => r.id !== selectedReservation?.id));
  //       alert('BOL issued successfully');
  //     }

  //     // ✅ NEW — حفظ/تحديث في manifest_bols دائماً
  // try {
  //   await saveToManifestDirect(form, editingBol);
  // } catch (manifestErr) {
  //   console.error('Error saving to manifest_bols:', manifestErr);
  //   alert(manifestErr.message || 'تم حفظ البوليصة في bols، لكن حدث خطأ أثناء الحفظ في manifest_bols');
  // }

  //     setEditingBol(null);
  //     setSelectedReservation(null);
  //     setIsDirectIssuance(false);
  //     setForm(emptyForm);

  //   } catch (err) {
  //     console.error('Error saving BOL:', err);
  //     alert('Error issuing BOL');
  //   }
  // };

  // Save current master locally for batch submission
  const saveCurrentMaster = () => {
    if (isOperatorOnly) {
      const validationError = validateOperatorForm(form);
      if (validationError) return alert(validationError);
    } else if (!form.bolReference) {
      return alert('Please enter AWB Reference for the master before saving');
    }

    setMasters(prev => ([...prev, JSON.parse(JSON.stringify(form))]));
    // Hide the form after saving; open it again via + Add New Master AWB.
    setIsDirectIssuance(false);
    setEditingBol(null);
    setSelectedReservation(null);
    clearMasterOnly();
  };

  const removeSavedMaster = (idx) => {
    setMasters(prev => prev.filter((_, i) => i !== idx));
  };

  const editSavedMaster = (idx) => {
    const target = masters[idx];
    if (!target) return;
    setForm(JSON.parse(JSON.stringify(target)));
    removeSavedMaster(idx);
    setIsDirectIssuance(true);
  };

  const removeHouseFromForm = (houseIdx) => {
    setForm((prev) => ({
      ...prev,
      homes: prev.homes.filter((_, idx) => idx !== houseIdx),
    }));
  };

  const deleteCurrentMaster = async () => {
    if (editingBol?.id) {
      await deleteBol(editingBol.id);
      return;
    }

    if (!window.confirm('Delete current master form data?')) return;
    clearMasterOnly();
    setSelectedReservation(null);
    setIsDirectIssuance(false);
    setEditingBol(null);
  };

  const submitAllMastersNow = async () => {
    if (masters.length === 0) return alert('No saved masters to submit');

    const normalizeBolRef = (value) => String(value ?? '').trim();

    if (isOperatorOnly) {
      try {
        for (const f of masters) {
          await api.post('/bols/operator-submission', buildOperatorSubmissionPayload(f));
        }
        alert('All master AWBs submitted successfully ✅');
        setMasters([]);
        setIsDirectIssuance(false);
      } catch (err) {
        console.error('Error submitting saved masters:', err);
        alert(err?.message || 'Error submitting saved masters');
      }
      return;
    }

    try {
      const existingBols = await api.get('/bols');
      const existingBolByRef = new Map(
        (Array.isArray(existingBols) ? existingBols : [])
          .map((b) => [normalizeBolRef(b.bol_reference), b])
          .filter(([ref]) => Boolean(ref))
      );

      for (const f of masters) {
        const masterAwbMultiplier = (Array.isArray(f.homes) && f.homes.length > 0) ? f.homes.length : 1;
        const masterComputedFees = calculateSingleBolFees(
          getChargeableWeight(f),
          Number(f.numberOfPackages || 0),
          f.cargoType || (Array.isArray(f.cargoTypes) ? f.cargoTypes[0] : '') || 'GCR',
          f.services || [],
          f.packingItems || [],
          f.buildUpItems || [],
          masterAwbMultiplier
        );

        let housesComputedTotal = 0;
        const homesWithComputedFees = (f.homes || []).map((h) => {
          const houseFees = h.fees || calculateSingleBolFees(
            getChargeableWeight(h),
            Number(h.numberOfPackages || 0),
            h.cargoType || f.cargoType || (Array.isArray(f.cargoTypes) ? f.cargoTypes[0] : '') || 'GCR',
            h.services || [],
            h.packingItems || [],
            h.buildUpItems || [],
            1
          );
          housesComputedTotal += Number(houseFees?.total || 0);
          return { ...h, fees: houseFees };
        });

        const aggregatedMasterTotal = Number(masterComputedFees?.total || 0) + housesComputedTotal;
        const forceAggregateForMaster = Array.isArray(f.homes) && f.homes.length > 0;
        const masterFeesToPersist = {
          ...(f.fees || masterComputedFees),
          total: forceAggregateForMaster
            ? Number(aggregatedMasterTotal)
            : Number(f?.fees?.total ?? aggregatedMasterTotal),
        };
        const fWithComputedFees = {
          ...f,
          fees: masterFeesToPersist,
          homes: homesWithComputedFees,
        };

        const currentBolRef = normalizeBolRef(fWithComputedFees.bolReference);
        const existingMasterBol = existingBolByRef.get(currentBolRef);

        let reservationId = existingMasterBol?.reservation_id || null;
        if (!reservationId) {
          const createdReservation = await api.post('/reservations', {
            customerName: fWithComputedFees.customerName || fWithComputedFees.ownerOperator || 'Master Entry',
            cargoTypes: fWithComputedFees.cargoTypes || [],
            weight: getChargeableWeight(fWithComputedFees),
            packages: fWithComputedFees.numberOfPackages || 0,
            services: (fWithComputedFees.services || []).map(s => s.id),
            packingItems: (fWithComputedFees.packingItems || []).map(p => p.id),
            buildUpItems: (fWithComputedFees.buildUpItems || []).map(b => b.id),
            storage_start_date: fWithComputedFees.storageStartDate || null,
            storage_end_date: fWithComputedFees.storageEndDate || fWithComputedFees.dateOfDeparture || null,
            notes: `Bulk master entry: ${fWithComputedFees.bolReference || ''}`,
          });

          reservationId = createdReservation?.id;
          if (!reservationId) {
            throw new Error('Reservation creation failed before AWB submission');
          }
        }

        // build payload similar to saveBOL
        const payload = {
          reservation_id: reservationId,
          bol_reference: fWithComputedFees.bolReference,
          status: 'services_assigned',
          owner_operator: fWithComputedFees.ownerOperator || null,
          aircraft_registration: fWithComputedFees.registration || null,
          declared_total_awbs: getDeclaredTotalAwbs(fWithComputedFees),
          customs_office_code: fWithComputedFees.customsOfficeCode || null,
          port_of_loading: fWithComputedFees.placeOfDepartureCode || null,
          port_of_discharge: fWithComputedFees.placeOfDestinationCode || null,
          bol_nature: fWithComputedFees.bolNature,
          bol_type_code: fWithComputedFees.homes && fWithComputedFees.homes.length > 0 ? '741' : '740',
          number_of_packages: fWithComputedFees.numberOfPackages,
          package_type_code: fWithComputedFees.packageTypeCode,
          gross_mass: fWithComputedFees.grossMass,
          weight: fWithComputedFees.weight || null,
          vol_by_mc: fWithComputedFees.volByMc || null,
          shipping_marks: fWithComputedFees.shippingMarks,
          goods_description: fWithComputedFees.goodsDescription,
          cargo_types: fWithComputedFees.cargoTypes,
          service_ids: (fWithComputedFees.services || []).map(s => s.id),
          packing_item_ids: (fWithComputedFees.packingItems || []).map(p => p.id),
          build_up_item_ids: (fWithComputedFees.buildUpItems || []).map(b => b.id),
          homes: (fWithComputedFees.homes || []).map(h => ({
            ...h,
            date_of_departure: fWithComputedFees.dateOfDeparture || null,
            etd: fWithComputedFees.etd,
            voyage_flight_no: fWithComputedFees.voyageFlightNo,
            storage_start_date: h.storageStartDate || fWithComputedFees.storageStartDate || null,
            storage_end_date: h.storageEndDate || fWithComputedFees.storageEndDate || fWithComputedFees.dateOfDeparture || null,
            weight: h.weight || null,
            vol_by_mc: h.volByMc || null,
          })),
          service_id: fWithComputedFees.services && fWithComputedFees.services.length > 0 ? fWithComputedFees.services[0].id : null,
          packing_item_id: fWithComputedFees.packingItems && fWithComputedFees.packingItems.length > 0 ? fWithComputedFees.packingItems[0].id : null,
          build_up_item_id: fWithComputedFees.buildUpItems && fWithComputedFees.buildUpItems.length > 0 ? fWithComputedFees.buildUpItems[0].id : null,
          storage_start_date: fWithComputedFees.storageStartDate || null,
          storage_end_date: fWithComputedFees.storageEndDate || fWithComputedFees.dateOfDeparture || null,
          date_of_departure: fWithComputedFees.dateOfDeparture || null,
          etd: fWithComputedFees.etd || null,
          voyage_flight_no: fWithComputedFees.voyageFlightNo || null,
          day: fWithComputedFees.day,
          fees: masterFeesToPersist,
          manifest_no: fWithComputedFees.manifestNo,
          customs_office_code: fWithComputedFees.customsOfficeCode
        };

        if (existingMasterBol?.id) {
          await api.put(`/bols/${existingMasterBol.id}`, payload);
        } else {
          const createdBol = await api.post('/bols', payload);
          const createdId = createdBol?.id;
          if (createdId && currentBolRef) {
            existingBolByRef.set(currentBolRef, { id: createdId, bol_reference: currentBolRef, reservation_id: reservationId });
          }
        }

        // Keep services flow consistent with operator flow by persisting into manifest tables too.
        await saveToManifestDirect(fWithComputedFees, null);
      }
      alert('Saved masters submitted successfully ✅');
      setMasters([]);
      setIsDirectIssuance(false);
      setSelectedManifestId(null);
      setForm(prev => ({ ...prev, manifestNo: '' }));
      await fetchBols();
      await fetchManifests();
    } catch (err) {
      console.error('Error submitting saved masters:', err);
      alert(err?.message || 'Error submitting saved masters');
    }
  };

  const calculateReceiptPricing = (bol) => {
    const storedFees = typeof bol?.fees === 'string' ? parseJsonValue(bol.fees) : (bol?.fees || {});
    const storedTotal = Number(storedFees.total);
    if (Number.isFinite(storedTotal) && storedTotal > 0) {
      return {
        service: Number(storedFees.service ?? storedFees.serviceFee ?? 0) || 0,
        packing: Number(storedFees.packing ?? storedFees.packingFee ?? 0) || 0,
        buildUp: Number(storedFees.buildUp ?? storedFees.buildUpFee ?? 0) || 0,
        total: storedTotal,
      };
    }

    const resolveItems = (source, listKey, idsKey, idKey, catalog) => {
      const directItems = Array.isArray(source?.[listKey]) ? source[listKey].filter(Boolean) : [];
      if (directItems.length > 0) return directItems;

      const ids = [...parseIdArray(source?.[idsKey]), ...parseIdArray(source?.[idKey])]
        .map(Number)
        .filter((id) => Number.isFinite(id) && id > 0);
      return ids.map((id) => (catalog || []).find((item) => Number(item?.id) === id)).filter(Boolean);
    };

    const computeLine = (source, awbCount = 1) => calculateSingleBolFees(
      getChargeableWeight(source),
      Number(source?.numberOfPackages ?? source?.number_of_packages ?? 0) || 0,
      source?.cargoType || source?.cargo_type || 'GCR',
      resolveItems(source, 'services', 'service_ids', 'service_id', servicesList),
      resolveItems(source, 'packingItems', 'packing_item_ids', 'packing_item_id', packingItems),
      resolveItems(source, 'buildUpItems', 'build_up_item_ids', 'build_up_item_id', buildUpItems),
      awbCount
    );

    const homes = Array.isArray(bol?.homes) ? bol.homes : [];
    const master = computeLine(bol, homes.length > 0 ? homes.length : 1);
    return homes.reduce((total, house) => {
      const fees = computeLine(house, 1);
      total.service += Number(fees.service || 0);
      total.packing += Number(fees.packing || 0);
      total.buildUp += Number(fees.buildUp || 0);
      total.total += Number(fees.total || 0);
      return total;
    }, {
      service: Number(master.service || 0),
      packing: Number(master.packing || 0),
      buildUp: Number(master.buildUp || 0),
      total: Number(master.total || 0),
    });
  };

  const openPrintOverrideModal = async (bol, mode = 'receipt') => {
    let pricingSource = bol;
    if (mode === 'invoice' && (bol?.bol_reference || bol?.bolReference)) {
      try {
        const reprintRows = await api.get(`/bols/for-reprint/${bol.bol_reference || bol.bolReference}`);
        const persistedBol = Array.isArray(reprintRows) ? reprintRows[0]?.bol : null;
        if (persistedBol) pricingSource = persistedBol;
      } catch (err) {
        console.warn('Could not reload persisted storage invoice pricing:', err);
      }
    }

    const receiptPricing = mode === 'receipt' ? calculateReceiptPricing(bol) : null;
    const receiptFinalAmount = Number(
      pricingSource?.receipt_final_amount ?? pricingSource?.receiptFinalAmount ??
      pricingSource?.receipt_amount ?? pricingSource?.receiptAmount ?? 0
    ) || 0;
    const baseAmount = mode === 'receipt'
      ? receiptPricing.total
      : receiptFinalAmount;
    const finalAmount = mode === 'receipt'
      ? baseAmount
      : receiptFinalAmount;

    setPrintOverrideModal({
      open: true,
      mode,
      bol,
      currency: printCurrency,
      baseAmount: String(baseAmount),
      finalAmount: String(finalAmount),
      feeBreakdown: receiptPricing,
    });
  };

  const closePrintOverrideModal = () => setPrintOverrideModal({ open: false, mode: 'receipt', bol: null, currency: printCurrency, baseAmount: '', finalAmount: '' });

  // ✅ طباعة الإيصال — بحفظ إجمالي رسوم الخدمات (Service + Packing + Build-up)
  const printReceipt = async (bol, override = null) => {
    try {
      // ─── 1️⃣ التحقق من تواريخ التخزين ───
      const v = validateStorageDates(bol, form);
      if (!v.valid) {
        alert(v.message + '\nيرجى تصحيح تواريخ التخزين قبل الطباعة.');
        return;
      }

      // // ─── 2️⃣ التحقق من وجود manifest مرتبط ───
      // if (!bol.manifest_id && !selectedManifestId && !form.manifestNo?.trim()) {
      //   alert('يرجى حفظ المنفست أولاً قبل طباعة الإيصال');
      //   return;
      // }

      // ─── 3️⃣ ✅ إعادة حساب رسوم الإيصال بنفس منطق الشاشة: Chargeable Weight = الأكبر بين Gross و VOL BY MC ───
      const parsedFees = typeof bol?.fees === 'string' ? parseJsonValue(bol.fees) : (bol?.fees || {});

      const resolveItems = (source, listKey, idsKey, idKey, catalog) => {
        const directItems = Array.isArray(source?.[listKey]) ? source[listKey].filter(Boolean) : [];
        if (directItems.length > 0) return directItems;

        const ids = [
          ...parseIdArray(source?.[idsKey]),
          ...parseIdArray(source?.[idKey])
        ]
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0);

        if (ids.length === 0) return [];
        return ids
          .map((id) => (catalog || []).find((x) => Number(x?.id) === id))
          .filter(Boolean);
      };

      const computeLineFees = (source, awbCount = 1) => {
        const chargeableWeight = getChargeableWeight(source);
        const qty = Number(source?.numberOfPackages ?? source?.number_of_packages ?? 0) || 0;
        const cargoType = source?.cargoType || source?.cargo_type || 'GCR';

        const sourceServices = resolveItems(source, 'services', 'service_ids', 'service_id', servicesList);
        const sourcePacking = resolveItems(source, 'packingItems', 'packing_item_ids', 'packing_item_id', packingItems);
        const sourceBuildUp = resolveItems(source, 'buildUpItems', 'build_up_item_ids', 'build_up_item_id', buildUpItems);

        return calculateSingleBolFees(
          chargeableWeight,
          qty,
          cargoType,
          sourceServices,
          sourcePacking,
          sourceBuildUp,
          awbCount
        );
      };

      const normalizeBolRef = (value) => String(value ?? '').trim();
      const masterRef = normalizeBolRef(bol?.bol_reference || bol?.bolReference);
      const homesRaw = Array.isArray(bol?.homes) ? bol.homes : [];
      const seenHouseKeys = new Set();
      const homes = homesRaw.filter((h) => {
        const ref = normalizeBolRef(h?.bol_reference || h?.bolReference);
        const idKey = String(h?.id ?? h?.bol_id ?? '');
        const dedupeKey = `${idKey}|${ref}`;
        if (ref && masterRef && ref === masterRef) return false;
        if (seenHouseKeys.has(dedupeKey)) return false;
        seenHouseKeys.add(dedupeKey);
        return true;
      });
      const masterAwbMultiplier = homes.length > 0 ? homes.length : 1;
      const masterComputed = computeLineFees(bol, masterAwbMultiplier);
      const housesComputed = homes.reduce(
        (acc, h) => {
          const line = computeLineFees(h, 1);
          acc.service += Number(line?.service || 0);
          acc.packing += Number(line?.packing || 0);
          acc.buildUp += Number(line?.buildUp || 0);
          acc.total += Number(line?.total || 0);
          return acc;
        },
        { service: 0, packing: 0, buildUp: 0, total: 0 }
      );

      const computedServiceFee = Number(masterComputed?.service || 0) + Number(housesComputed?.service || 0);
      const computedPackingFee = Number(masterComputed?.packing || 0) + Number(housesComputed?.packing || 0);
      const computedBuildUpFee = Number(masterComputed?.buildUp || 0) + Number(housesComputed?.buildUp || 0);
      const computedReceiptAmount = Number(masterComputed?.total || 0) + Number(housesComputed?.total || 0);

      const storedReceiptAmount = Number(parsedFees?.total ?? bol?.receipt_amount ?? 0);
      const receiptPricing = calculateReceiptPricing(bol);
      const serviceFee = receiptPricing.service || computedServiceFee;
      const packingFee = receiptPricing.packing || computedPackingFee;
      const buildUpFee = receiptPricing.buildUp || computedBuildUpFee;
      const receiptBaseAmount = override?.baseAmount !== undefined && override.baseAmount !== ''
        ? Number(override.baseAmount)
        : (receiptPricing.total || computedReceiptAmount || storedReceiptAmount);
      const receiptAmount = override?.finalAmount !== undefined && override.finalAmount !== ''
        ? Number(override.finalAmount)
        : receiptBaseAmount;

      console.log('=== Receipt Amount Calculation ===');
      console.log('Service Fee:', serviceFee);
      console.log('Packing Fee:', packingFee);
      console.log('Build-up Fee:', buildUpFee);
      console.log('Total Receipt Amount:', receiptAmount);
      console.log('calculatedFees:', calculatedFees);

      // ─── 4️⃣ تسجيل الدفع مع تمرير المبلغ والعملة ───
      let receiptRes;
      receiptRes = await api.post(`/bols/${bol.id}/pay`, {
        receiptAmount: receiptAmount,
        receiptBaseAmount: receiptBaseAmount,
        serviceFee: serviceFee,
        packingFee: packingFee,
        buildUpFee: buildUpFee,
        amountType: override?.currency || printCurrency,
        currencyType: override?.currency || printCurrency
      });

      // ─── 5️⃣ إعادة جلب البيانات ───
      await fetchBols();
      await fetchManifests();

      const currencyForPrint = override?.currency || printCurrency;

      // ─── 6️⃣ تحضير بيانات الإيصال للطباعة ───
      const responseBol = receiptRes?.bol || receiptRes;
      const finalReceiptNo = receiptRes?.receipt_no
        || receiptRes?.bol?.receipt_no
        || responseBol?.receipt_no;
      const finalReceiptAmount = Number(receiptRes?.receipt_amount || receiptAmount || 0);
      const printBol = {
        ...bol,
        ...responseBol,
        receipt_base_amount: receiptBaseAmount,
        receipt_final_amount: finalReceiptAmount,
        consignee_name:
          responseBol?.consignee_name ||
          bol?.consignee_name ||
          responseBol?.consigneeName ||
          bol?.consigneeName ||
          responseBol?.customer_name ||
          bol?.customer_name ||
          "",
      };

      // ─── 7️⃣ طباعة الإيصال ───
      generateReceiptPdf({
        bol: {
          ...printBol,
          receipt_no: finalReceiptNo,
          receipt_amount: finalReceiptAmount,
          amount_type: currencyForPrint,
          fees: {
            service: serviceFee,
            packing: packingFee,
            buildUp: buildUpFee,
            total: finalReceiptAmount
          }
        },
        savedReceiptAmount: finalReceiptAmount,
        currency: currencyForPrint,
        exchangeRate: exchangeRate
      });

      if (editingBol && Number(editingBol.id) === Number(bol.id)) {
        setEditingBol((prev) => ({
          ...prev,
          paid: 1,
          receipt_no: finalReceiptNo,
          receipt_amount: finalReceiptAmount,
          fees: {
            service: serviceFee,
            packing: packingFee,
            buildUp: buildUpFee,
            total: finalReceiptAmount
          },
        }));
      }

    } catch (err) {
      console.error('Error printing receipt:', err);
      alert(err.message || 'خطأ في طباعة الإيصال');
    }
  };

  // ✅ طباعة الفاتورة — إنشاء رقم فاتورة + اعتبار البضاعة مشحونة للطائرة
  const printInvoice = async (bol, override = null) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // 1️⃣ إنشاء سجل الفاتورة في billing_documents (نفس طريقة Manifest)
      const invoicePayload = {
        document_type: 'INVOICE',
        master_bol_id: bol.parent_bol_id || bol.id,
        bol_ids: bol.parent_bol_id
          ? `${bol.parent_bol_id},${bol.id}`
          : String(bol.id),
        total_amount: bol.fees?.total || 0,
        created_by: user.id || 1,
      };

      const invoiceDoc = await api.post('/billing-documents', invoicePayload);

      // 2️⃣ تحديث البوليصة: رقم الفاتورة + اعتبارها مشحونة للطائرة
      await api.put(`/bols/${bol.id}`, {
        invoice_no: invoiceDoc.document_number,
        is_in_manifest: 1,
      });

      // 3️⃣ تحديث القائمة المحلية
      await fetchBols();

      const currencyForPrint = override?.currency || printCurrency;
      const finalStorageAmount = override?.amount != null && override.amount !== '' ? Number(override.amount) : null;

      // 4️⃣ الطباعة
      const printData = {
        ...bol,
        invoice_no: invoiceDoc.document_number,
        fees: parseJsonValue(bol.fees),
        homes: (bol.homes || []).map(h => ({
          ...h,
          fees: parseJsonValue(h.fees)
        }))
      };
      const resolvedBaseAmount = Number(bol?.storage_base_amount ?? bol?.storageBaseAmount ?? 0) || 0;
      const resolvedEffectiveAmount = (finalStorageAmount ?? Number(bol?.storage_final_amount ?? bol?.storageFinalAmount ?? bol?.storage_amount ?? bol?.storageFee ?? bol?.storage_fee ?? 0)) || 0;
      const printDataWithOverride = {
        ...printData,
        storage_base_amount: resolvedBaseAmount,
        storageBaseAmount: resolvedBaseAmount,
        storage_final_amount: resolvedEffectiveAmount,
        storageFinalAmount: resolvedEffectiveAmount,
        storage_amount: resolvedEffectiveAmount,
        storageFee: resolvedEffectiveAmount,
        storage_fee: resolvedEffectiveAmount,
      };

      generateBolInvoicePdf({
        bol: printDataWithOverride,
        servicesList,
        packingItems,
        buildUpItems,
        currency: currencyForPrint,
        exchangeRate: exchangeRate,
      });
    } catch (err) {
      console.error('Error creating invoice:', err);
      alert('Error creating invoice');
    }
  };



  const printManifestInvoiceAndTakeoff = async (manifestId) => {
    if (!window.confirm('سيتم إصدار فاتورة التخزين لكل البوالص وتأكيد إقلاع الرحلة. هل تريد المتابعة؟')) return;
    try {
      await api.post(`/manifests/${manifestId}/takeoff`);
      const full = await api.get(`/manifests/${manifestId}/full`);
      const masterBols = (full.bols || []).filter((b) => !b.parent_bol_id);

      masterBols.forEach((m) => {
        const homes = m.homes || [];
        const total =
          Number(m.invoice_amount || 0) +
          homes.reduce((sum, h) => sum + Number(h.invoice_amount || 0), 0);

        generateBolInvoicePdf({
          bol: { ...m, homes },
          header: full.header,
          days: m.billable_days,
          d_no: m.d_no,
          savedInvoiceAmount: total,
          servicesList,
          packingItems,
          buildUpItems,
        });
      });

      alert('تم إصدار فاتورة التخزين وتأكيد إقلاع الرحلة بنجاح ✅');
      await fetchManifests();
    } catch (err) {
      console.error('Error issuing storage invoice / takeoff:', err);
      alert(err.message || 'خطأ في إصدار فاتورة التخزين وتأكيد الإقلاع');
    }
  };


  // ✅ تحويل general من الـ form لصيغة الـ /manifest API
  const buildManifestHeaderPayload = (f) => ({
    ownerOperator: f.ownerOperator || '',
    registration: f.registration || '',
    flightNo: f.voyageFlightNo || '',
    voyageNumber: f.voyageFlightNo || '',
    customsOfficeCode: f.customsOfficeCode || '',
    placeOfDepartureCode: f.placeOfDepartureCode || '',
    placeOfDestinationCode: f.placeOfDestinationCode || '',
    dateOfDeparture: f.dateOfDeparture || null,
    dateOfArrival: f.storageEndDate || f.dateOfDeparture || null,
    manifestNo: f.manifestNo || null,
  });

  const buildManifestBolPayload = (f) => {
    const masterCalc = buildStorageCalcFields(f);
    const masterAwbMultiplier = (Array.isArray(f.homes) && f.homes.length > 0) ? f.homes.length : 1;
    const masterComputedFees = calculateSingleBolFees(
      getChargeableWeight(f),
      Number(f.numberOfPackages || 0),
      f.cargoType || (Array.isArray(f.cargoTypes) ? f.cargoTypes[0] : '') || 'GCR',
      f.services || [],
      f.packingItems || [],
      f.buildUpItems || [],
      masterAwbMultiplier
    );
    const housesComputedTotal = (f.homes || []).reduce((sum, h) => {
      const houseComputed = h?.fees || calculateSingleBolFees(
        getChargeableWeight(h),
        Number(h.numberOfPackages || 0),
        h.cargoType || f.cargoType || (Array.isArray(f.cargoTypes) ? f.cargoTypes[0] : '') || 'GCR',
        h.services || [],
        h.packingItems || [],
        h.buildUpItems || [],
        1
      );
      return sum + Number(houseComputed?.total || 0);
    }, 0);
    const aggregatedMasterTotal = Number(masterComputedFees?.total || 0) + housesComputedTotal;
    const forceAggregateForMaster = Array.isArray(f.homes) && f.homes.length > 0;
    const masterReceiptAmount = forceAggregateForMaster
      ? Number(aggregatedMasterTotal)
      : Number(
        f?.receiptAmount ??
        f?.receipt_amount ??
        f?.fees?.total ??
        aggregatedMasterTotal
      );

    return {
      id: f.id || null,
      // ─── البيانات الأساسية ───
      bolReference: f.bolReference,
      cargoType: f.cargoType || (f.cargoTypes && f.cargoTypes[0]) || 'GCR',
      bolNature: f.bolNature || '23',
      bolTypeCode: f.homes && f.homes.length > 0 ? '741' : '740',
      placeOfLoadingCode: f.placeOfDepartureCode || '',
      placeOfUnloadingCode: f.placeOfDestinationCode || '',
      exporterName: f.exporterName || '',
      exporterAddress: f.exporterAddress || '',
      consigneeName: f.consigneeName || f.customerName || '',
      consigneeAddress: f.consigneeAddress || '',
      notifyName: f.notifyName || '',
      notifyAddress: f.notifyAddress || '',
      numberOfPackages: f.numberOfPackages,
      packageTypeCode: f.packageTypeCode,
      grossMass: f.grossMass,
      weight: f.weight || null,
      volByMc: f.volByMc || null,
      vol_by_mc: f.volByMc || null,
      shippingMarks: f.shippingMarks,
      goodsDescription: f.goodsDescription,
      serviceIds: (f.services || []).map(s => s.id),
      packingItemIds: (f.packingItems || []).map(p => p.id),
      buildUpItemIds: (f.buildUpItems || []).map(b => b.id),
      day: !!f.day,
      etd: f.etd || null,
      storageStartDate: f.storageStartDate,
      storageEndDate: f.storageEndDate || f.dateOfDeparture,

      // ─── حقول التخزين (camelCase للباك اند) ───
      freePeriodHours: masterCalc.freePeriodHours,
      storageDaysTotal: masterCalc.storageDaysTotal,
      billableDays: masterCalc.billableDays,
      storageFee: masterCalc.storageFinalAmount,
      storageBaseAmount: masterCalc.storageBaseAmount,
      storageFinalAmount: masterCalc.storageFinalAmount,

      // ─── حقول التخزين (snake_case مباشرة لقاعدة البيانات) ───
      free_period_hours: masterCalc.freePeriodHours,
      storage_days_total: masterCalc.storageDaysTotal,
      billable_days: masterCalc.billableDays,
      storage_amount: masterCalc.storageFinalAmount,
      storage_base_amount: masterCalc.storageBaseAmount,
      storage_final_amount: masterCalc.storageFinalAmount,
      receiptAmount: masterReceiptAmount,
      receipt_amount: masterReceiptAmount,

      // ─── الهاوسات ───
      homes: (f.homes || []).map(h => {
        const houseCalc = buildStorageCalcFields(h);
        const houseComputedFees = calculateSingleBolFees(
          getChargeableWeight(h),
          Number(h.numberOfPackages || 0),
          h.cargoType || f.cargoType || (Array.isArray(f.cargoTypes) ? f.cargoTypes[0] : '') || 'GCR',
          h.services || [],
          h.packingItems || [],
          h.buildUpItems || [],
          1
        );
        const houseReceiptAmount = Number(h?.receiptAmount ?? h?.receipt_amount ?? h?.fees?.total ?? houseComputedFees.total ?? 0);
        return {
          id: h.id || (h.tempId && !String(h.tempId).startsWith('new_') ? Number(h.tempId) : null),
          bolReference: h.bolReference,
          cargoType: h.cargoType || f.cargoType || 'GCR',
          bolNature: h.bolNature || f.bolNature || '23',
          bolTypeCode: '740',
          exporterName: h.exporterName || f.exporterName || '',
          exporterAddress: h.exporterAddress || f.exporterAddress || '',
          consigneeName: h.consigneeName || '',
          consigneeAddress: h.consigneeAddress || '',
          notifyName: h.notifyName || f.notifyName || '',
          notifyAddress: h.notifyAddress || f.notifyAddress || '',
          numberOfPackages: h.numberOfPackages,
          packageTypeCode: h.packageTypeCode || f.packageTypeCode,
          grossMass: h.grossMass,
          weight: h.weight || null,
          volByMc: h.volByMc || null,
          vol_by_mc: h.volByMc || null,
          shippingMarks: h.shippingMarks,
          goodsDescription: h.goodsDescription,
          serviceIds: (h.services || []).map(s => s.id),
          packingItemIds: (h.packingItems || []).map(p => p.id),
          buildUpItemIds: (h.buildUpItems || []).map(b => b.id),
          day: !!h.day,
          etd: h.etd || f.etd || null,
          storageStartDate: h.storageStartDate || f.storageStartDate,
          storageEndDate: h.storageEndDate || f.storageEndDate || f.dateOfDeparture,

          // camelCase
          freePeriodHours: houseCalc.freePeriodHours,
          storageDaysTotal: houseCalc.storageDaysTotal,
          billableDays: houseCalc.billableDays,
          storageFee: houseCalc.storageFinalAmount,
          storageBaseAmount: houseCalc.storageBaseAmount,
          storageFinalAmount: houseCalc.storageFinalAmount,

          // snake_case
          free_period_hours: houseCalc.freePeriodHours,
          storage_days_total: houseCalc.storageDaysTotal,
          billable_days: houseCalc.billableDays,
          storage_amount: houseCalc.storageFinalAmount,
          storage_base_amount: houseCalc.storageBaseAmount,
          storage_final_amount: houseCalc.storageFinalAmount,
          receiptAmount: houseReceiptAmount,
          receipt_amount: houseReceiptAmount,
        };
      }),
    };
  };

  // ✅ تحويل بوليصة قديمة راجعة من /manifests/:id/full (snake_case) إلى camelCase
  // (بدونها الحقول القديمة بتترّجع NULL عند التحديث - باگ موجود في الباك اند)
  const mapExistingManifestBolForSave = (b) => ({
    id: b.id,
    bolReference: b.bol_reference,
    cargoType: b.cargo_type,
    lineNumber: b.line_number,
    bolNature: b.bol_nature,
    bolTypeCode: b.bol_type_code,
    placeOfLoadingCode: b.place_of_loading_code,
    placeOfUnloadingCode: b.place_of_unloading_code,
    exporterName: b.exporter_name,
    exporterAddress: b.exporter_address,
    consigneeName: b.consignee_name,
    consigneeAddress: b.consignee_address,
    notifyName: b.notify_name,
    notifyAddress: b.notify_address,
    numberOfPackages: b.number_of_packages,
    packageTypeCode: b.package_type_code,
    grossMass: b.gross_mass,
    weight: b.weight,
    volByMc: b.vol_by_mc,
    shippingMarks: b.shipping_marks,
    goodsDescription: b.goods_description,
    numOfCtnForThisBol: b.num_of_ctn_for_this_bol,
    serviceIds: parseIdArray(b.service_ids ?? b.service_id),
    packingItemIds: parseIdArray(b.packing_item_ids ?? b.packing_item_id),
    buildUpItemIds: parseIdArray(b.build_up_item_ids ?? b.build_up_item_id),
    day: !!b.day,
    etd: b.etd || null,
    storageStartDate: b.storage_start_date,
    storageEndDate: b.storage_end_date,
    receiptAmount: b.receipt_amount,
    receipt_amount: b.receipt_amount,
    homes: Array.isArray(b.homes) ? b.homes.map(mapExistingManifestBolForSave) : [],
  });

  // ✅ حفظ/تحديث البوليصة (ماستر+هاوسات) في المنفست باستخدام المنطق القديم POST /manifest أو PUT /manifests/:id
  const saveToManifestOldLogic = async (f) => {
    const bolPayload = buildManifestBolPayload(f);

    if (selectedManifestId) {
      // منفست موجود: نجلب بياناته الكاملة، ندمج البوليصة الحالية معه، ثم نحدّث
      const existing = await api.get(`/manifests/${selectedManifestId}/full`);
      const existingBols = Array.isArray(existing.bols) ? existing.bols.map(mapExistingManifestBolForSave) : [];
      const incomingRef = String(bolPayload.bolReference || '').trim();
      const filteredExisting = existingBols.filter(
        (b) => String(b.bolReference || '').trim() !== incomingRef
      );
      const updatePayload = {
        general: buildManifestHeaderPayload(f),
        bols: [...filteredExisting, bolPayload],
      };
      await api.put(`/manifest/${selectedManifestId}`, updatePayload);
      await fetchManifests();
      return { headerId: selectedManifestId };
    } else {
      const payload = {
        general: buildManifestHeaderPayload(f),
        bols: [bolPayload],
      };
      const res = await api.post('/manifest', payload);
      const headerId = res.headerId || res.id;
      if (headerId) setSelectedManifestId(headerId);
      await fetchManifests();
      return { headerId };
    }
  };


  const saveToManifestDirect = async (f, editingBolData) => {
    const bolPayload = buildManifestBolPayload({
      ...f,
      id: f.id || editingBolData?.id || null,
    });

    // 1) البحث عن المنفست باستخدام manifestNo
    let targetHeaderId =
      selectedManifestId ||
      editingBolData?.manifest_id ||
      editingBolData?.header_id ||
      null;

    if (!targetHeaderId && f.manifestNo) {
      try {
        const allManifests = await api.get('/manifests/list');
        const matched = allManifests.find(m =>
          String(m.manifest_no).trim() === String(f.manifestNo).trim() ||
          String(m.id) === String(f.manifestNo).trim()
        );
        if (matched) {
          targetHeaderId = matched.id;
          setSelectedManifestId(matched.id);
        }
      } catch (e) {
        console.log('Error fetching manifests list:', e);
      }
    }

    // 2) إذا لم يُوجد منفست، أنشئ جديداً
    if (!targetHeaderId) {
      const newPayload = {
        general: buildManifestHeaderPayload(f),
        bols: [bolPayload],
      };
      const res = await api.post('/manifest', newPayload);
      targetHeaderId = res.headerId || res.id;
      if (targetHeaderId) {
        setSelectedManifestId(targetHeaderId);
        setForm(prev => ({ ...prev, manifestNo: String(targetHeaderId) }));
      }
      await fetchManifests();
      return { headerId: targetHeaderId };
    }

    // 3) إذا وجد منفست، جلبه ودمج البوليصة الحالية معه
    const existing = await api.get(`/manifests/${targetHeaderId}/full`);
    const existingBols = Array.isArray(existing.bols)
      ? existing.bols.map(mapExistingManifestBolForSave)
      : [];

    const currentBolRef = String(bolPayload.bolReference || '').trim();
    const targetBolId = Number(editingBolData?.id || f?.id || bolPayload?.id || 0);
    const existingIndexById = targetBolId > 0
      ? existingBols.findIndex((b) => Number(b?.id || 0) === targetBolId)
      : -1;
    const existingIndexByRef = existingBols.findIndex(
      (b) => String(b.bolReference || '').trim() === currentBolRef
    );
    const existingIndex = existingIndexById >= 0 ? existingIndexById : existingIndexByRef;

    let updatedBols;
    if (existingIndex >= 0) {
      // تحديث البوليصة الموجودة مع الحفاظ على الـ ID
      updatedBols = [...existingBols];
      const existingHomes = Array.isArray(updatedBols[existingIndex]?.homes)
        ? updatedBols[existingIndex].homes
        : [];
      const incomingHomes = Array.isArray(bolPayload?.homes)
        ? bolPayload.homes
        : [];
      // إذا وصلت homes فارغة أثناء تعديل الماستر، لا نحذف الهاوسات المخزنة مسبقاً.
      const mergedHomes = incomingHomes.length > 0 ? incomingHomes : existingHomes;
      updatedBols[existingIndex] = {
        ...updatedBols[existingIndex],
        ...bolPayload,
        homes: mergedHomes,
        id: updatedBols[existingIndex].id || updatedBols[existingIndex].bol_id || null,
      };
    } else {
      // إضافة بوليصة جديدة
      updatedBols = [...existingBols, bolPayload];
    }

    const updatePayload = {
      general: buildManifestHeaderPayload(f),
      bols: updatedBols,
    };

    await api.put(`/manifest/${targetHeaderId}`, updatePayload);
    await fetchManifests();
    return { headerId: targetHeaderId };
  };


  const saveManifestBols = async (form, calculatedFees) => {
    const masterCalc = buildStorageCalcFields(form);

    console.log('🔍 masterCalc:', masterCalc);
    console.log('🔍 form.storageStartDate:', form.storageStartDate);
    console.log('🔍 form.storageEndDate:', form.storageEndDate);
    console.log('🔍 form.cargoType:', form.cargoType);

    const payload = {
      general: buildManifestHeaderPayload(form),
      bols: [{
        bolReference: form.bolReference,
        cargoType: form.cargoType || 'GCR',
        bolNature: form.bolNature || '23',
        bolTypeCode: form.homes && form.homes.length > 0 ? '741' : '740',
        numberOfPackages: form.numberOfPackages,
        packageTypeCode: form.packageTypeCode || '',
        grossMass: form.grossMass,
        weight: form.weight || null,
        volByMc: form.volByMc || null,
        vol_by_mc: form.volByMc || null,
        shippingMarks: form.shippingMarks || '',
        goodsDescription: form.goodsDescription || '',
        serviceIds: (form.services || []).map(s => s.id),
        packingItemIds: (form.packingItems || []).map(p => p.id),
        buildUpItemIds: (form.buildUpItems || []).map(b => b.id),
        day: !!form.day,
        storageStartDate: form.storageStartDate,
        storageEndDate: form.storageEndDate || form.dateOfDeparture,

        // ✅ حقول التخزين — نجرب camelCase فقط أولاً
        freePeriodHours: masterCalc.freePeriodHours,
        storageDaysTotal: masterCalc.storageDaysTotal,
        billableDays: masterCalc.billableDays,
        storageFee: masterCalc.storageFee,
        receiptAmount: Number(calculatedFees?.total || 0),

        homes: (form.homes || []).map(h => {
          const houseCalc = buildStorageCalcFields(h);
          return {
            bolReference: h.bolReference,
            cargoType: h.cargoType || form.cargoType || 'GCR',
            bolTypeCode: '740',
            numberOfPackages: h.numberOfPackages,
            grossMass: h.grossMass,
            weight: h.weight || null,
            volByMc: h.volByMc || null,
            vol_by_mc: h.volByMc || null,
            goodsDescription: h.goodsDescription || '',
            serviceIds: (h.services || []).map(s => s.id),
            packingItemIds: (h.packingItems || []).map(p => p.id),
            buildUpItemIds: (h.buildUpItems || []).map(b => b.id),
            day: !!h.day,
            storageStartDate: h.storageStartDate || form.storageStartDate,
            storageEndDate: h.storageEndDate || form.storageEndDate || form.dateOfDeparture,
            freePeriodHours: houseCalc.freePeriodHours,
            storageDaysTotal: houseCalc.storageDaysTotal,
            billableDays: houseCalc.billableDays,
            storageFee: houseCalc.storageFee,
            receiptAmount: Number(h?.fees?.total || 0),
          };
        })
      }]
    };

    console.log('📦 Payload bols[0]:', JSON.stringify(payload.bols[0], null, 2));

    try {
      const res = await api.post('/manifest', payload);
      console.log('✅ Response:', res);
      const headerId = res.headerId || res.id;
      if (!headerId) throw new Error('No headerId returned from server');
      return { headerId, ...res };
    } catch (err) {
      console.error('❌ Error:', err.response?.data || err.message);
      throw err;
    }
  };

  const openManifestIds = new Set((manifests || []).map((m) => Number(m.id)));
  const getBolManifestHeaderId = (bol) => {
    const candidates = [bol?.manifest_id, bol?.header_id, bol?.manifest_no];
    for (const value of candidates) {
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
  };

  return (
    <div className="page">
      <h2>{isOperatorOnly ? 'AWB Entry' : 'AWB Issuance'}</h2>

      <div className="card" style={{ border: '2px solid #4caf50', background: '#f1f8e9', marginBottom: '16px' }}>
        <h3>Flight/Voyage Information (Shared for all Masters)</h3>
        <div className="grid grid-auto">
          <div className="field">
            <label>Customer Name (Acceptance) *</label>
            <input type="text" value={form.customerName} onChange={(e) => handleFormChange('customerName', e.target.value)} />
          </div>
          <div className="field">
            <label>Owner / Operator *</label>
            <input type="text" value={form.ownerOperator} onChange={(e) => handleFormChange('ownerOperator', e.target.value)} />
          </div>
          <div className="field">
            <label>Aircraft Registration *</label>
            <input type="text" value={form.registration} onChange={(e) => handleFormChange('registration', e.target.value)} />
          </div>
          <div className="field">
            <label>Customs Office Code</label>
            <input type="text" value={form.customsOfficeCode} onChange={(e) => handleFormChange('customsOfficeCode', e.target.value)} />
          </div>
          {canEditServices && (
            <>
              <div className="field">
                <label>Manifest Number / Name</label>
                <input
                  type="text"
                  value={form.manifestNo}
                  onChange={(e) => {
                    handleFormChange('manifestNo', e.target.value);
                    if (selectedManifestId) setSelectedManifestId(null);
                  }}
                  placeholder="Enter new manifest number/name"
                />
              </div>
            </>
          )}
          <div className="field">
            <label>Flight No.</label>
            <input type="text" value={form.voyageFlightNo} onChange={(e) => handleFormChange('voyageFlightNo', e.target.value)} />
          </div>
          <div className="field">
            <label>Date of Departure</label>
            <input type="date" value={form.dateOfDeparture} onChange={(e) => handleFormChange('dateOfDeparture', e.target.value)} />
          </div>
          <div className="field">
            <label>ETD</label>
            <input type="time" value={form.etd} onChange={(e) => handleFormChange('etd', e.target.value)} />
          </div>
          <div className="field">
            <label>Point of Loading *</label>
            <input type="text" value={form.placeOfDepartureCode} onChange={(e) => handleFormChange('placeOfDepartureCode', e.target.value)} />
          </div>
          <div className="field">
            <label>Point of Discharge *</label>
            <input type="text" value={form.placeOfDestinationCode} onChange={(e) => handleFormChange('placeOfDestinationCode', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="actions-row" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" onClick={startNewMasterEntry}>
            + Add New Master AWB
          </button>
        </div>
      </div>

      {masters.length > 0 && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Saved Master AWBs ({masters.length})</h3>
            <button className="btn btn-success" onClick={submitAllMastersNow} type="button">Submit All Masters</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {masters.map((m, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff' }}>
                <div>
                  <strong style={{ color: '#0d47a1' }}>{m.bolReference}</strong>
                  <span style={{ marginLeft: 10, color: '#666' }}>
                    — {m.numberOfPackages} pkg / {m.grossMass} kg — Houses: {m.homes?.length || 0}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => editSavedMaster(idx)} type="button">Edit</button>
                  <button className="btn btn-danger" onClick={() => removeSavedMaster(idx)} type="button">Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(selectedReservation || editingBol || isDirectIssuance) && (
        <div className="card" style={{ border: '2px solid #2196f3', background: '#f0f7ff' }}>
          <h3>
            {editingBol
              ? `Edit AWB ${editingBol.bol_reference}`
              : selectedReservation
                ? `Issue AWB for Reservation #${selectedReservation.id}`
                : isOperatorOnly
                  ? 'New Operator AWB Entry'
                  : 'New AWB Issuance'}
          </h3>

          <div className="grid grid-auto">

            <div className="field">
              <label>AWB Reference *</label>
              <input
                type="text"
                value={form.bolReference}
                onChange={(e) => handleFormChange('bolReference', e.target.value)}
                style={{ background: '#f5f5f5' }}
              />
            </div>
            <div className="field">
              <label>Number of Packages</label>
              <input
                type="number"
                value={form.numberOfPackages}
                onChange={(e) => handleFormChange('numberOfPackages', e.target.value)}
                readOnly={form.homes.length > 0}
                style={form.homes.length > 0 ? { background: '#e9ecef' } : undefined}
              />
            </div>

            <div className="field">
              <label>Gross Mass</label>
              <input
                type="number"
                value={form.grossMass}
                onChange={(e) => handleFormChange('grossMass', e.target.value)}
                readOnly={form.homes.length > 0}
                style={form.homes.length > 0 ? { background: '#e9ecef' } : undefined}
              />
            </div>

            <div className="field">
              <label>Weight</label>
              <input
                type="number"
                value={form.weight}
                onChange={(e) => handleFormChange('weight', e.target.value)}
              />
            </div>

            <div className="field">
              <label>VOL BY MC</label>
              <input
                type="number"
                value={form.volByMc}
                onChange={(e) => handleFormChange('volByMc', e.target.value)}
                readOnly={form.homes.length > 0}
                style={form.homes.length > 0 ? { background: '#e9ecef' } : undefined}
              />
            </div>


            <div className="field">
              <label>Goods Description</label>
              <input type="text" value={form.goodsDescription} onChange={(e) => handleFormChange('goodsDescription', e.target.value)} />
            </div>

            {canEditServices && (
              <>
                <div className="field" style={{ gridColumn: '1 / -1' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowMasterStorageDetails((prev) => !prev)}
                  >
                    {showMasterStorageDetails ? 'HIDE' : 'SHOW'}
                  </button>
                </div>

                {showMasterStorageDetails && (
                  <>
                    <div className="field">
                      <label>Free Period (Hours)</label>
                      <input
                        type="text"
                        value={`${parseFreeHours(form.cargoType)} hours`}
                        readOnly
                        style={{ background: '#f5f5f5', fontWeight: 'bold', color: '#1976d2' }}
                      />
                    </div>

                    <div className="field">
                      <label>Storage Days (Total / Billable)</label>
                      <input
                        type="text"
                        value={(() => {
                          const disp = getLiveStorageDisplay(form);
                          return `${disp.totalDays} / ${disp.billableDays}`;
                        })()}
                        readOnly
                        style={{ background: '#f5f5f5', fontWeight: 'bold', color: '#2e7d32' }}
                      />
                    </div>

                    <div className="field">
                      <label>Base Storage Amount</label>
                      <input
                        type="number"
                        value={form.storageBaseAmount ?? ''}
                        onChange={(e) => handleFormChange('storageBaseAmount', e.target.value)}
                        style={{ background: '#f5f5f5', fontWeight: 'bold', color: '#1976d2' }}
                      />
                    </div>

                    <div className="field">
                      <label>Final Storage Amount</label>
                      <input
                        type="number"
                        value={form.storageFinalAmount ?? ''}
                        onChange={(e) => handleFormChange('storageFinalAmount', e.target.value)}
                        style={{ background: '#f5f5f5', fontWeight: 'bold', color: '#d32f2f' }}
                      />
                    </div>

                    <div className="field">
                      <label>Storage Fee (Calculated)</label>
                      <input
                        type="text"
                        value={(() => {
                          const disp = getLiveStorageDisplay(form);
                          return `$${Number(disp.finalFee || 0).toFixed(2)}`;
                        })()}
                        readOnly
                        style={{ background: '#f5f5f5', fontWeight: 'bold', color: '#d32f2f' }}
                      />
                    </div>

                    <div className="field">
                      <label>Master Service Fee (Per AWB)</label>
                      <input
                        type="text"
                        value={`$${calculatePerAwbServiceFee(form.services, form.cargoType, form.homes?.length > 0 ? form.homes.length + 1 : 1).toFixed(2)}`}
                        readOnly
                        style={{ background: '#f5f5f5', fontWeight: 'bold', color: '#7c3aed' }}
                      />
                    </div>

                    {form.homes.length > 0 && (() => {
                      const summary = getHousesStorageSummary(form);
                      return summary ? (
                        <>
                          <div className="field">
                            <label>Houses Cargo Types (Summary)</label>
                            <input value={summary.cargoTypes} readOnly style={{ background: '#fff7e6', fontWeight: 'bold' }} />
                          </div>
                          <div className="field">
                            <label>Houses Total Gross Mass (kg)</label>
                            <input value={summary.totalMass.toFixed(2)} readOnly style={{ background: '#fff7e6', fontWeight: 'bold' }} />
                          </div>
                          <div className="field">
                            <label>Houses Total Storage Fee</label>
                            <input value={`$${summary.totalFee.toFixed(2)}`} readOnly style={{ background: '#fff7e6', fontWeight: 'bold', color: '#d32f2f' }} />
                          </div>
                        </>
                      ) : null;
                    })()}

                    <div className="field">
                      <label>Cargo Type</label>
                      <select value={form.cargoType} onChange={(e) => handleFormChange('cargoType', e.target.value)}>
                        <option value="">Select Cargo Type</option>
                        {cargoTypes.map(ct => (
                          <option key={ct.id} value={ct.name}>{ct.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="field">
                      <label>Gross Mass (kg)</label>
                      <input type="number" value={form.grossMass} onChange={(e) => handleFormChange('grossMass', e.target.value)} />
                    </div>
                  </>
                )}
              </>
            )}

            <div className="field">
              <label>Cargo Types</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                {form.cargoTypes.map((type, idx) => (
                  <span key={idx} style={{ background: '#c8dbb8', borderRadius: 16, padding: '2px 8px', display: 'inline-flex', alignItems: 'center' }}>
                    {type}
                    <button
                      onClick={() => setForm(f => {
                        const updatedCargoTypes = f.cargoTypes.filter(t => t !== type);
                        const updatedCargoType = updatedCargoTypes.includes(f.cargoType) ? f.cargoType : (updatedCargoTypes[0] || '');
                        return { ...f, cargoTypes: updatedCargoTypes, cargoType: updatedCargoType };
                      })}
                      style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}
                    >×</button>
                  </span>
                ))}
              </div>
              <div className="select-field">
                <select value="" onChange={e => {
                  const val = e.target.value;
                  if (val && !form.cargoTypes.includes(val)) {
                    setForm(f => {
                      const updatedCargoTypes = [...f.cargoTypes, val];
                      const updatedCargoType = f.cargoType || val;
                      return { ...f, cargoTypes: updatedCargoTypes, cargoType: updatedCargoType };
                    });
                  }
                }}>
                  <option value="">Select Cargo Type</option>
                  {cargoTypes.filter(ct => !form.cargoTypes.includes(ct.name)).map(ct => (
                    <option key={ct.id} value={ct.name}>{ct.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {canEditServices && (
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Services Selection (Auto-identifies classification by weight)</label>
                <div className="selected-items-container" style={{ flexDirection: 'column', gap: '10px', background: '#fff', padding: '10px' }}>
                  {form.services.length === 0 && (
                    <span className="hint-text" style={{ padding: '5px' }}>No services selected yet...</span>
                  )}
                  {form.services.map(srvc => (
                    <div key={srvc.id} style={{ display: 'flex', flexDirection: 'column', gap: '5px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span><strong>{srvc.type}:</strong> {srvc.classification}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', color: '#2e7d32', fontWeight: 'bold' }}>
                            <input
                              type="checkbox"
                              checked={srvc.showPacking || false}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                const newServices = form.services.map(s => s.id === srvc.id ? { ...s, showPacking: checked } : s);
                                setForm(f => {
                                  const updatedPacking = (!checked && srvc.selectedPackingId)
                                    ? f.packingItems.filter(p => p.id !== srvc.selectedPackingId)
                                    : f.packingItems;
                                  return { ...f, services: newServices, packingItems: updatedPacking };
                                });
                              }}
                            /> Add Packing
                          </label>
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => {
                              setForm(f => {
                                const updatedServices = f.services.filter(s => s.id !== srvc.id);
                                const updatedPacking = srvc.selectedPackingId
                                  ? f.packingItems.filter(p => p.id !== srvc.selectedPackingId)
                                  : f.packingItems;
                                return { ...f, services: updatedServices, packingItems: updatedPacking };
                              });
                            }}
                          >✕</button>
                        </div>
                      </div>
                      {srvc.showPacking && (
                        <div className="select-field" style={{ marginTop: '5px' }}>
                          {srvc.selectedPackingId ? (
                            <div className="item-chip" style={{ background: '#e2e8f0', justifyContent: 'space-between', display: 'flex', padding: '5px 10px' }}>
                              <span><strong>Packing:</strong> {(() => {
                                const p = packingItems.find(item => item.id == srvc.selectedPackingId);
                                return p ? `${p.type}${p.classification ? ` - ${p.classification}` : ''}` : '';
                              })()}</span>
                              <button type="button" className="remove-btn" onClick={() => {
                                setForm(f => ({
                                  ...f,
                                  services: f.services.map(s => s.id === srvc.id ? { ...s, selectedPackingId: null } : s),
                                  packingItems: f.packingItems.filter(p => p.id !== srvc.selectedPackingId)
                                }));
                              }}>✕</button>
                            </div>
                          ) : (
                            <select value="" onChange={e => {
                              const item = packingItems.find(p => p.id == e.target.value);
                              if (item) {
                                setForm(f => {
                                  const updatedServices = f.services.map(s => s.id === srvc.id ? { ...s, selectedPackingId: item.id } : s);
                                  const updatedPacking = !f.packingItems.some(p => p.id == item.id) ? [...f.packingItems, item] : f.packingItems;
                                  return { ...f, services: updatedServices, packingItems: updatedPacking };
                                });
                              }
                            }}>
                              <option value="">Select Master Packing Type...</option>
                              {packingItems.filter(p => !form.packingItems.some(selected => selected.id === p.id)).map(p => (
                                <option key={p.id} value={p.id}>{p.type} - {p.classification}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="select-field">
                  <select value="" onChange={e => handleAutoServiceSelect(e.target.value)}>
                    <option value="">+ Add Service Type...</option>
                    {uniqueServiceTypes
                      .filter(t => !form.services.some(s => s.type === t))
                      .map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                  </select>
                </div>
              </div>
            )}

            {canEditServices && (
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Master Packing Summary</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, minHeight: '35px', padding: '5px', background: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                  {form.packingItems.length === 0 && <span className="hint-text">No packing selected</span>}
                  {form.packingItems.map(item => (
                    <span
                      key={item.id}
                      style={{ background: '#c8dbb8', borderRadius: 16, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', fontSize: '11px' }}
                    >
                      {item.type}{item.classification ? ` - ${item.classification}` : ''}
                      <button
                        onClick={() => setForm(f => ({ ...f, packingItems: f.packingItems.filter(p => p.id !== item.id) }))}
                        style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}
                      >×</button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {canEditServices && (
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Build-up Selection</label>
                <div className="selected-items-container">
                  {form.buildUpItems.map(item => (
                    <div key={item.id} className="item-chip">
                      {item.name}
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => setForm(f => ({ ...f, buildUpItems: f.buildUpItems.filter(b => b.id !== item.id) }))}
                      >✕</button>
                    </div>
                  ))}
                </div>
                <div className="select-field">
                  <select value="" onChange={e => {
                    const item = buildUpItems.find(b => b.id == e.target.value);
                    if (item && !form.buildUpItems.some(b => b.id === item.id)) {
                      setForm(f => ({ ...f, buildUpItems: [...f.buildUpItems, item] }));
                    }
                  }}>
                    <option value="">+ Add Build-up Item...</option>
                    {buildUpItems.filter(b => !form.buildUpItems.some(selected => selected.id === b.id)).map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Houses (Master BOLs)</label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    const newHouse = {
                      tempId: `new_${Date.now()}`,
                      bolReference: '',
                      grossMass: '',
                      weight: '',
                      volByMc: '',
                      numberOfPackages: '',
                      goodsDescription: '',
                      cargoType: '',
                      services: [],
                      packingItems: [],
                      buildUpItems: []
                    };
                    setForm(f => ({ ...f, homes: [...f.homes, newHouse] }));
                  }}
                >+ Add House</button>
                {canEditServices && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowHousesStorageDetails((prev) => !prev)}
                  >
                    {showHousesStorageDetails ? 'HIDE' : 'SHOW'}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {form.homes.map((house, idx) => (
                  <div key={house.tempId} className="card" style={{
                    marginBottom: '15px',
                    border: '1px solid #0288d1',
                    borderLeft: '6px solid #0288d1',
                    padding: '20px',
                    background: '#f0f9ff'
                  }}>
                    <div className="card-header-row">
                      <strong style={{ color: '#0288d1' }}>
                        <span className="badge" style={{ background: '#0288d1', color: 'white' }}>HOUSE</span>
                        BOL #{idx + 1}
                      </strong>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => removeHouseFromForm(idx)}
                      >
                        Remove House
                      </button>
                    </div>
                    <div className="grid grid-auto small-gap" style={{ gridColumn: '1 / -1' }}>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>House Ref *</label>
                        <input type="text" value={house.bolReference} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].bolReference = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }} />
                      </div>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>House Gross Mass (kg) *</label>
                        <input type="number" value={house.grossMass} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].grossMass = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }} />
                      </div>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>Weight (kg)</label>
                        <input type="number" value={house.weight || ''} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].weight = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }} />
                      </div>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>VOL BY MC</label>
                        <input type="number" value={house.volByMc || ''} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].volByMc = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }} />
                      </div>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>House Packages *</label>
                        <input type="number" value={house.numberOfPackages} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].numberOfPackages = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }} />
                      </div>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>Goods Description</label>
                        <input type="text" value={house.goodsDescription} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].goodsDescription = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }} />
                      </div>
                      {canEditServices && showHousesStorageDetails && (
                        <>
                          <div className="field">
                            <label>Cargo Type</label>
                            <select value={house.cargoType || ''} onChange={(e) => {
                              const newHomes = [...form.homes];
                              newHomes[idx].cargoType = e.target.value;
                              setForm(f => ({ ...f, homes: newHomes }));
                            }}>
                              <option value="">Select Cargo Type</option>
                              {cargoTypes.map(ct => (
                                <option key={ct.id} value={ct.name}>{ct.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="field">
                            <label>Free Period (Hours)</label>
                            <input type="text" readOnly value={`${parseFreeHours(house.cargoType)} hours`} style={{ background: '#f5f5f5' }} />
                          </div>
                          <div className="field">
                            <label>Storage Days (Total / Billable)</label>
                            <input type="text" readOnly value={(() => {
                              const d = getStorageDisplay(house);
                              return `${d.totalDays} / ${d.billableDays}`;
                            })()} style={{ background: '#f5f5f5' }} />
                          </div>
                          <div className="field">
                            <label>Storage Fee (Calculated)</label>
                            <input type="text" readOnly value={`$${getStorageDisplay(house).fee.toFixed(2)}`} style={{ background: '#f5f5f5', color: '#d32f2f' }} />
                          </div>
                        </>
                      )}
                    </div>

                    {canEditServices && (
                      <div className="grid grid-auto small-gap" style={{ gridColumn: '1 / -1' }}>
                        <div className="field">
                          <label>House Build-up</label>
                          <div className="selected-items-container">
                            {house.buildUpItems?.map(b => (
                              <div key={b.id} className="item-chip" style={{ background: '#fff' }}>
                                <small>{b.name}</small>
                                <button type="button" className="remove-btn" onClick={() => {
                                  const newHomes = [...form.homes];
                                  newHomes[idx].buildUpItems = house.buildUpItems.filter(item => item.id !== b.id);
                                  setForm(f => ({ ...f, homes: newHomes }));
                                }}>✕</button>
                              </div>
                            ))}
                          </div>
                          <div className="select-field">
                            <select value="" onChange={e => {
                              const item = buildUpItems.find(b => b.id == e.target.value);
                              if (item) {
                                const newHomes = [...form.homes];
                                newHomes[idx].buildUpItems = [...(house.buildUpItems || []), item];
                                setForm(f => ({ ...f, homes: newHomes }));
                              }
                            }}>
                              <option value="">+ Add Build-up...</option>
                              {buildUpItems.filter(b => !house.buildUpItems?.some(hi => hi.id === b.id)).map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}

                    {canEditServices && (
                      <div className="grid grid-auto small-gap" style={{ marginTop: '10px', gridColumn: '1 / -1' }}>
                        <div className="field">
                          <label>House Services & Packing</label>
                          <div className="selected-items-container" style={{ flexDirection: 'column', gap: '8px' }}>
                            {house.services.map(srv => (
                              <div key={srv.id} style={{ background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #bae6fd', width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <small><strong>{srv.type}:</strong> {srv.classification}</small>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', color: '#0288d1', cursor: 'pointer' }}>
                                      <input
                                        type="checkbox"
                                        checked={srv.showPacking || false}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setForm(f => {
                                            const newHomes = [...f.homes];
                                            newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, showPacking: checked } : s);
                                            if (!checked && srv.selectedPackingId) {
                                              newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                            }
                                            return { ...f, homes: newHomes };
                                          });
                                        }}
                                      /> Add Packing
                                    </label>
                                    <button type="button" className="remove-btn" onClick={() => {
                                      setForm(f => {
                                        const newHomes = [...f.homes];
                                        newHomes[idx].services = house.services.filter(s => s.id !== srv.id);
                                        if (srv.selectedPackingId) {
                                          newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                        }
                                        return { ...f, homes: newHomes };
                                      });
                                    }}>✕</button>
                                  </div>
                                </div>
                                {srv.showPacking && (
                                  <div className="select-field" style={{ marginTop: '5px' }}>
                                    {srv.selectedPackingId ? (
                                      <div className="item-chip" style={{ background: '#f1f5f9', justifyContent: 'space-between', display: 'flex', padding: '4px 8px' }}>
                                        <small><strong>Packing:</strong> {(() => {
                                          const p = packingItems.find(item => item.id == srv.selectedPackingId);
                                          return p ? `${p.type}${p.classification ? ` - ${p.classification}` : ''}` : '';
                                        })()}</small>
                                        <button type="button" className="remove-btn" onClick={() => {
                                          const newHomes = [...form.homes];
                                          newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, selectedPackingId: null } : s);
                                          newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                          setForm(f => ({ ...f, homes: newHomes }));
                                        }}>✕</button>
                                      </div>
                                    ) : (
                                      <select value="" onChange={e => {
                                        const item = packingItems.find(p => p.id == e.target.value);
                                        if (item) {
                                          const newHomes = [...form.homes];
                                          newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, selectedPackingId: item.id } : s);
                                          if (!house.packingItems.some(p => p.id == item.id)) {
                                            newHomes[idx].packingItems = [...house.packingItems, item];
                                          }
                                          setForm(f => ({ ...f, homes: newHomes }));
                                        }
                                      }}>
                                        <option value="">Select House Packing...</option>
                                        {packingItems.filter(p => !house.packingItems.some(hi => hi.id === p.id)).map(p => (
                                          <option key={p.id} value={p.id}>{p.type} - {p.classification}</option>
                                        ))}
                                      </select>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="select-field" style={{ marginTop: '8px' }}>
                            <select value="" onChange={e => handleHouseAutoServiceSelect(idx, e.target.value)}>
                              <option value="">+ Add Service to House...</option>
                              {uniqueServiceTypes.filter(t => !house.services.some(s => s.type === t)).map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="field">
                          <label>House Packing Summary</label>
                          <div className="selected-items-container">
                            {house.packingItems.map(p => (
                              <div key={p.id} className="item-chip" style={{ background: '#fff' }}>
                                <small>{p.type}{p.classification ? ` - ${p.classification}` : ''}</small>
                                <button type="button" className="remove-btn" onClick={() => {
                                  const newHomes = [...form.homes];
                                  newHomes[idx].packingItems = house.packingItems.filter(item => item.id !== p.id);
                                  setForm(f => ({ ...f, homes: newHomes }));
                                }}>✕</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {canEditServices && (
            <div className="calc-result">
              <div>Service Fee: ${calculatedFees.service.toFixed(2)}</div>
              <div>Packing Fee: ${calculatedFees.packing.toFixed(2)}</div>
              <div>Build-up Fee: ${calculatedFees.buildUp.toFixed(2)}</div>
              <div>Total: ${calculatedFees.total.toFixed(2)}</div>
            </div>
          )}

          <div className="actions-row">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={saveCurrentMaster} type="button" disabled={savingManifest}>
                Save This Master
              </button>

              {masters.length > 0 && (
                <button className="btn btn-success" onClick={submitAllMastersNow} type="button" disabled={savingManifest}>
                  Submit All Masters
                </button>
              )}

              {/* <button
                className="btn"
                onClick={handleSaveManifest}
                disabled={savingManifest}
                style={{
                  backgroundColor: savingManifest
                    ? '#0666a7'
                    : selectedManifestId
                      ? '#43a047'
                      : '#0288d1',
                  color: '#fff',
                  opacity: savingManifest ? 0.7 : 1
                }}
              >
                {savingManifest
                  ? '⏳ SAVING...'
                  : !canEditServices
                    ? editingBol
                      ? '🔄 UPDATE AWB'
                      : '💾 SUBMIT AWB'
                    : selectedManifestId
                    ? '🔄 UPDATE MANIFEST'
                    : '💾 SAVE MANIFEST'}
              </button> */}

            </div>

            {editingBol && (
              <>
                <button className="btn btn-danger" style={{ marginLeft: '10px' }} onClick={deleteCurrentMaster}>
                  Remove Master
                </button>
                <button className="btn btn-secondary" style={{ marginLeft: '10px' }} onClick={cancelEdit}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {printOverrideModal.open && printOverrideModal.bol && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3>{printOverrideModal.mode === 'receipt' ? 'Receipt Amount' : 'Storage Invoice Amount'}</h3>
            </div>
            <div className="modal-body">
              <div className="field">
                <label>Currency</label>
                <select
                  value={printOverrideModal.currency}
                  onChange={(e) => setPrintOverrideModal((prev) => ({ ...prev, currency: e.target.value }))}
                >
                  <option value="USD">USD</option>
                  <option value="SDG">SDG</option>
                </select>
              </div>
              {printOverrideModal.mode === 'receipt' && printOverrideModal.feeBreakdown && (
                <div className="calc-result" style={{ marginBottom: 12 }}>
                  <div>Service Fee: ${printOverrideModal.feeBreakdown.service.toFixed(2)}</div>
                  <div>Packing Fee: ${printOverrideModal.feeBreakdown.packing.toFixed(2)}</div>
                  <div>Build-up Fee: ${printOverrideModal.feeBreakdown.buildUp.toFixed(2)}</div>
                  <div>Total: ${printOverrideModal.feeBreakdown.total.toFixed(2)}</div>
                </div>
              )}
              <div className="field">
                <label>{printOverrideModal.mode === 'receipt' ? 'Base Receipt Amount' : 'Discounted Receipt Amount'}</label>
                <input type="number" step="0.01" value={printOverrideModal.baseAmount} readOnly />
              </div>
              {printOverrideModal.mode === 'receipt' && (
              <div className="field">
                <label>Final Receipt Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={printOverrideModal.finalAmount}
                  onChange={(e) => setPrintOverrideModal((prev) => ({ ...prev, finalAmount: e.target.value }))}
                />
              </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" type="button" onClick={closePrintOverrideModal}>Cancel</button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  const override = {
                    currency: printOverrideModal.currency,
                    baseAmount: printOverrideModal.baseAmount,
                    finalAmount: printOverrideModal.finalAmount,
                    amount: printOverrideModal.finalAmount,
                  };
                  closePrintOverrideModal();
                  if (printOverrideModal.mode === 'receipt') {
                    printReceipt(printOverrideModal.bol, override);
                  } else {
                    printStorageInvoice(printOverrideModal.bol, override);
                  }
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {canEditServices && (
        <div className="card">
          <h3>AWBs Awaiting Services</h3>

          {masters.length > 0 && (
            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h4>Saved Master AWBs ({masters.length})</h4>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-success" onClick={submitAllMastersNow} type="button">Submit All Saved</button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {masters.map((m, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', border: '1px solid #e0e0e0', borderRadius: 6 }}>
                    <div>
                      <strong>{m.bolReference}</strong> — {m.customerName || m.ownerOperator} — {m.numberOfPackages}/{m.grossMass}kg — Houses: {m.homes?.length || 0}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-secondary" onClick={() => { setForm(JSON.parse(JSON.stringify(m))); removeSavedMaster(idx); }}>Edit</button>
                      <button className="btn btn-danger" onClick={() => removeSavedMaster(idx)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <table className="grid-table" style={{ marginBottom: '30px' }}>
            <thead>
              <tr>
                <th>AWB Reference</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bols.filter(b => !b.paid && b.status !== 'services_assigned' && (b.returned_for_review == null || b.returned_for_review == 0)).map(b => (
                <tr key={b.id}>
                  <td>{b.bol_reference}</td>
                  <td><span className="badge badge-dg">{b.status === 'operator_review' ? 'Returned for Review' : 'Pending Services'}</span></td>
                  <td>
                    <button className="btn btn-warning" onClick={() => editBOL(b)}>Select Services</button>
                    <button className="btn btn-danger" style={{ marginLeft: '8px' }} onClick={() => deleteBol(b.id)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '2px solid #eee', paddingTop: '20px', marginBottom: '15px', marginTop: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <h3 style={{ color: '#0288d1', margin: 0 }}>AWBs Ready for Receipt Payment</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#e3f2fd', padding: '6px 14px', borderRadius: '6px', border: '1px solid #90caf9' }}>
              <span style={{ fontWeight: 'bold', color: '#1565c0', fontSize: '14px' }}>Print & Payment Currency:</span>
              <label style={{ cursor: 'pointer', fontWeight: printCurrency === 'USD' ? 'bold' : 'normal', color: '#0d47a1', display: 'flex', alignItems: 'center', gap: '4px', margin: 0 }}>
                <input
                  type="radio"
                  name="printCurrency"
                  value="USD"
                  checked={printCurrency === 'USD'}
                  onChange={() => setPrintCurrency('USD')}
                />
                USD ($)
              </label>
              <label style={{ cursor: 'pointer', fontWeight: printCurrency === 'SDG' ? 'bold' : 'normal', color: '#0d47a1', display: 'flex', alignItems: 'center', gap: '4px', margin: 0 }}>
                <input
                  type="radio"
                  name="printCurrency"
                  value="SDG"
                  checked={printCurrency === 'SDG'}
                  onChange={() => setPrintCurrency('SDG')}
                />
                SDG (Rate: {exchangeRate})
              </label>
            </div>
          </div>

          <table className="grid-table" style={{ marginBottom: '30px' }}>
            <thead>
              <tr>
                <th>AWB Reference</th>
                <th>Houses</th>
                <th>Packages</th>
                <th>Gross Mass</th>
                <th>Goods Description</th>
                <th>Total Fees</th>
                <th>Receipt</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bols
                .filter(b => !b.parent_bol_id && b.status === 'services_assigned')
                .filter((b) => {
                  const manifestHeaderId = getBolManifestHeaderId(b);
                  return !manifestHeaderId || openManifestIds.has(manifestHeaderId);
                })
                .map(b => (
                  <tr key={b.id}>
                    <td>{b.bol_reference}</td>
                    <td>{b.homes?.length || 0}</td>
                    <td>{b.number_of_packages}</td>
                    <td>{b.gross_mass}</td>
                    <td>{b.goods_description}</td>
                    <td>{(b.fees?.total ?? 0).toFixed ? b.fees.total.toFixed(2) : b.fees?.total}</td>
                    <td>
                      {b.paid
                        ? `No: ${b.receipt_no || '-'} | Amount: ${(Number(b.receipt_amount || b.fees?.total || 0)).toFixed(2)}`
                        : '-'}
                    </td>
                    <td>
                      <button className="btn btn-warning" onClick={() => editBOL(b)}>
                        Edit
                      </button>
                      <button className="btn btn-primary" style={{ marginLeft: '8px' }} onClick={() => openPrintOverrideModal(b, 'receipt')}>
                        Print Receipt
                      </button>
                      <button className="btn btn-success" style={{ marginLeft: '8px' }} onClick={() => printStorageInvoice(b)}>
                        Storage Invoice
                      </button>
                      {getBolManifestHeaderId(b) && (
                        <>
                          {/* <button className="btn" style={{ backgroundColor: '#1976d2', color: '#fff', marginLeft: b.paid ? '8px' : (!b.paid ? '8px' : '0') }} 
                          onClick={() => printManifestDocuments(getBolManifestHeaderId(b), 'load_breakdown')}>
                          Load Breakdown
                        </button> */}
                          {/* <button className="btn btn-secondary" style={{ marginLeft: '8px' }} 
                          onClick={() => handleAirCargoManifest(getBolManifestHeaderId(b))}>
                          Air Cargo Manifest
                        </button> */}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              {bols
                .filter(b => !b.parent_bol_id && b.status === 'services_assigned')
                .filter((b) => {
                  const manifestHeaderId = getBolManifestHeaderId(b);
                  return !manifestHeaderId || openManifestIds.has(manifestHeaderId);
                }).length === 0 && (
                  <tr><td colSpan={8}>No AWBs ready for receipt payment</td></tr>
                )}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '2px solid #eee', paddingTop: '20px', marginBottom: '15px' }}>
            <h3 style={{ color: '#2e7d32', margin: 0 }}>Issued & Ready for Printing</h3>
            <button
              className="btn btn-secondary"
              onClick={() => setShowIssuedSection(!showIssuedSection)}
              style={{ padding: '6px 12px', fontSize: '14px' }}
            >
              {showIssuedSection ? '▼ Hide' : '▶ Show'}
            </button>
          </div>

          {showIssuedSection && (
            <table className="grid-table">
              <thead>
                <tr>
                  <th>Manifest #</th>
                  <th>Voyage</th>
                  <th>Arrival</th>
                  <th>Masters</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {manifests.map((manifest) => {
                  const statusText = manifest.is_delivered
                    ? 'Delivered'
                    : 'Ready for Manifest Docs';

                  return (
                    <tr key={manifest.id}>
                      <td>{manifest.id}</td>
                      <td>{manifest.voyage_number || '-'}</td>
                      <td>{manifest.date_of_arrival || '-'}</td>
                      <td>{manifest.master_count || 0}</td>
                      <td>{statusText}</td>
                      <td>
                        <div style={{ display: 'inline-flex', gap: '8px', flexWrap: 'wrap' }}>
                          {/* <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setSelectedManifestId(manifest.id);
                            try { window.dispatchEvent(new CustomEvent('manifest:selected', { detail: { id: manifest.id } })); } catch(e) {}
                          }}
                        >
                          Select
                        </button> */}
                          <button
                            className="btn"
                            style={{ backgroundColor: '#1976d2', color: '#fff' }}
                            onClick={() => printManifestDocuments(manifest.id, 'load_breakdown')}
                          >
                            Load Breakdown
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleAirCargoManifest(manifest.id)}
                          >
                            Air Cargo Manifest
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default BOLIssuanceScreen;